<template>
  <BottomSheetModal 
    v-model="isOpen" 
    :confirmDisabled="!isFormValid || !isFormDirty"
    :hideHeader="currentView !== 'main'"
    @confirm="handleSave"
    @close="handleClose"
  >
    <template #title>{{ isEditMode ? $t('seasonForm.edit') : $t('seasonForm.new') }}</template>

    <!-- Tab bar (outside views-container so sticky works) -->
    <div v-show="currentView === 'main'" ref="seasonTabsRef" class="sf-tabs">
      <button
        v-for="tab in seasonTabs"
        :key="tab.key"
        :ref="(el) => { if (el) seasonTabRefs[tab.key] = el as HTMLElement }"
        class="sf-tab"
        :class="{ 'sf-tab--active': seasonTab === tab.key }"
        @click="seasonTab = tab.key"
      >
        {{ $t(tab.label) }}
      </button>
      <span class="sf-tabs__indicator" :style="seasonTabIndicatorStyle" />
    </div>

    <div class="views-container">
      <!-- Main View -->
      <div class="main-view" :class="{ 'main-view--hidden': currentView !== 'main' }">

        <!-- TAB: Obecné (General) -->
        <div v-show="seasonTab === 'general'" class="sf-tab-content">
        <!-- Season Type Section -->
        <IosSection form :title="$t('seasonForm.seasonTypeTitle')">
          <IosRow
            :label="$t('seasonForm.seasonTypeLabel')"
            :value="seasonTypeLabel"
            clickable
            first
            last
            @click="currentView = 'seasonType'"
          />
        </IosSection>

        <!-- Dates Section -->
        <IosSection form :title="$t('seasonForm.periodTitle')">
          <IosRow 
            :label="$t('seasonForm.startDate')"
            :value="form.dateFrom || $t('seasonForm.notSet')"
            first
          >
            <template #value>
              <input 
                type="date" 
                class="date-input"
                :value="form.dateFrom"
                @input="form.dateFrom = ($event.target as HTMLInputElement).value"
              />
            </template>
          </IosRow>
          <IosRow 
            :label="$t('seasonForm.endDate')"
            :value="form.dateTo || $t('seasonForm.notSet')"
          >
            <template #value>
              <input 
                type="date" 
                class="date-input"
                :value="form.dateTo"
                @input="form.dateTo = ($event.target as HTMLInputElement).value"
              />
            </template>
          </IosRow>
          <IosSwitch 
            v-model="form.active" 
            :label="$t('seasonForm.seasonActive')"
            last
          />
        </IosSection>

        <!-- Registration Fees Section -->
        <IosSection form :title="$t('seasonForm.entryFee')">
          <IosRow 
            :label="$t('seasonForm.playerFee')"
            first
          >
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.registrationFeePlayer"
                min="0"
              />
            </template>
          </IosRow>
          <IosRow 
            :label="$t('seasonForm.goalieFee')"
          >
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.registrationFeeGoalie"
                min="0"
              />
            </template>
          </IosRow>
          <IosRow 
            :label="$t('seasonForm.teamFee')"
            last
          >
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.registrationFeeTeam"
                min="0"
              />
            </template>
          </IosRow>
        </IosSection>

        <!-- Propozice -->
        <IosSection form :title="$t('seasonForm.propositionsTitle')">
          <div class="content-editor-wrapper">
            <textarea 
              v-model="form.content"
              class="content-textarea"
              :placeholder="$t('seasonForm.propositionsPlaceholder')"
              rows="6"
            ></textarea>
          </div>
        </IosSection>

        <!-- Global season stats -->
        <div v-if="form.playoffConfig.scenarioType" class="season-stats">
          <div class="season-stats__section">
            <div class="season-stats__header">{{ $t('seasonForm.statSectionOverview') }}</div>
            <div class="season-stats__grid">
              <div class="playoff-stat">
                <div class="playoff-stat__number">{{ totalTeamCount }}</div>
                <div class="playoff-stat__label">{{ $t('seasonForm.statTeams') }}</div>
              </div>
              <div v-if="hasGroups(form.playoffConfig.scenarioType)" class="playoff-stat">
                <div class="playoff-stat__number">{{ form.playoffConfig.groupCount }}×{{ teamsPerGroup }}</div>
                <div class="playoff-stat__label">{{ $t('seasonForm.statGroups') }}</div>
              </div>
              <div class="playoff-stat">
                <div class="playoff-stat__number">{{ form.playoffConfig.branches.length }}</div>
                <div class="playoff-stat__label">{{ $t('seasonForm.statBranches') }}</div>
              </div>
            </div>
          </div>
          <div v-if="regularSeasonMatchCount.total > 0" class="season-stats__section">
            <div class="season-stats__header">{{ $t('seasonForm.statSectionRegular') }}</div>
            <div class="season-stats__grid">
              <div v-if="form.groups.length > 1" class="playoff-stat">
                <div class="playoff-stat__number">{{ regularSeasonMatchCount.perGroup }}</div>
                <div class="playoff-stat__label">{{ $t('seasonForm.statMatchesPerGroup') }}</div>
              </div>
              <div class="playoff-stat">
                <div class="playoff-stat__number">{{ regularSeasonMatchCount.total }}</div>
                <div class="playoff-stat__label">{{ $t('seasonForm.statMatchCount') }}</div>
              </div>
            </div>
          </div>
          <div class="season-stats__section">
            <div class="season-stats__header">{{ $t('seasonForm.statSectionPlayoff') }}</div>
            <div class="season-stats__grid">
              <div class="playoff-stat">
                <div class="playoff-stat__number">{{ form.playoffConfig.branches.length > 0 ? form.playoffConfig.branches[0].teamCount : 0 }}</div>
                <div class="playoff-stat__label">{{ $t('seasonForm.statPlayoffTeams') }}</div>
              </div>
              <div class="playoff-stat">
                <div class="playoff-stat__number">{{ playoffMatchRange.min === playoffMatchRange.max ? playoffMatchRange.min : `${playoffMatchRange.min}–${playoffMatchRange.max}` }}</div>
                <div class="playoff-stat__label">{{ $t('seasonForm.statMatchCount') }}</div>
              </div>
              <div v-if="form.playoffConfig.placementMatches.length > 0" class="playoff-stat">
                <div class="playoff-stat__number">{{ form.playoffConfig.placementMatches.length }}</div>
                <div class="playoff-stat__label">{{ $t('seasonForm.statPlacementMatches') }}</div>
              </div>
            </div>
          </div>
          <div v-if="form.playoffConfig.consolationGroupEnabled && consolationTeamCount > 1" class="season-stats__section">
            <div class="season-stats__header">{{ form.playoffConfig.consolationGroupName || $t('seasonForm.statSectionConsolation') }}</div>
            <div class="season-stats__grid">
              <div class="playoff-stat">
                <div class="playoff-stat__number">{{ consolationTeamCount }}</div>
                <div class="playoff-stat__label">{{ $t('seasonForm.statTeams') }}</div>
              </div>
              <div class="playoff-stat">
                <div class="playoff-stat__number">{{ consolationMatchCount }}</div>
                <div class="playoff-stat__label">{{ $t('seasonForm.statMatchCount') }}</div>
              </div>
            </div>
          </div>
          <div v-if="regularSeasonMatchCount.total > 0" class="season-stats__section season-stats__section--total">
            <div class="season-stats__grid">
              <div class="playoff-stat">
                <div class="playoff-stat__number">{{ totalMatchCountLabel }}</div>
                <div class="playoff-stat__label">{{ $t('seasonForm.statTotalMatches') }}</div>
              </div>
              <div class="playoff-stat">
                <div class="playoff-stat__number">{{ totalPlayingTimeLabel }}</div>
                <div class="playoff-stat__label">{{ $t('seasonForm.statTotalPlayingTime') }}</div>
              </div>
            </div>
          </div>
        </div>

        </div><!-- /TAB: Obecné -->

        <!-- TAB: Základní část (Regular season) -->
        <div v-show="seasonTab === 'regular'" class="sf-tab-content">
        <!-- Regular Season Settings -->
        <IosSection form :title="$t('seasonForm.regularSeasonSettings')">
          <IosRow :label="$t('seasonForm.regularRounds')">
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.regularRounds"
                min="1"
              />
            </template>
          </IosRow>
          <IosSwitch 
            v-model="form.splitPointsByParts" 
            :label="$t('seasonForm.splitPoints')"
            :subtitle="$t('seasonForm.splitPointsDesc')"
          />
          <IosRow :label="$t('seasonForm.winPoints')">
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.pointsWin"
                min="0"
              />
            </template>
          </IosRow>
          <IosRow :label="$t('seasonForm.overtimeWinPoints')">
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.pointsOvertimeWin"
                min="0"
              />
            </template>
          </IosRow>
          <IosRow 
            :label="$t('seasonForm.overtimeLossPoints')"
            last
          >
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.pointsOvertimeLoss"
                min="0"
              />
            </template>
          </IosRow>
        </IosSection>

        <!-- Game Settings Section -->
        <IosSection form :title="$t('seasonForm.gameSettings')">
          <IosRow 
            :label="$t('seasonForm.periodsCount')"
            first
          >
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.partsCount"
                min="1"
                max="5"
              />
            </template>
          </IosRow>
          <IosRow :label="$t('seasonForm.periodLength')">
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.partTime"
                min="1"
              />
            </template>
          </IosRow>
          <IosRow 
            :label="$t('seasonForm.playersCount')"
            :value="form.playersCount"
            clickable
            @click="currentView = 'playersCount'"
          />
          <IosRow :label="$t('seasonForm.overtimeLength')">
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.overtimeDuration"
                min="0"
                :placeholder="$t('seasonForm.overtimePlaceholder')"
              />
            </template>
          </IosRow>
          <IosRow 
            :label="$t('seasonForm.minorPenalty')"
          >
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.penaltySmall"
                min="0"
                step="0.5"
              />
            </template>
          </IosRow>
          <IosRow :label="$t('seasonForm.majorPenalty')">
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.penaltyBig"
                min="0"
              />
            </template>
          </IosRow>
          <IosRow 
            :label="$t('seasonForm.misconductPenalty')"
            last
          >
            <template #value>
              <input 
                type="number" 
                class="number-input"
                v-model.number="form.penaltyPersonal"
                min="0"
              />
            </template>
          </IosRow>
        </IosSection>

        <!-- Tiebreaker criteria -->
        <IosSection form :title="$t('seasonForm.tiebreakersTitle')">
          <div class="tiebreakers-list">
            <div
              v-for="(tb, index) in form.tiebreakers"
              :key="tb"
              class="tiebreaker-item"
            >
              <span class="tiebreaker-index">{{ index + 1 }}.</span>
              <span class="tiebreaker-label">{{ $t(`seasonForm.tb_${tb}`) }}</span>
              <div class="tiebreaker-actions">
                <button
                  v-if="index > 0"
                  class="tiebreaker-btn"
                  @click="moveTiebreaker(index, -1)"
                >
                  <v-icon size="18">mdi-chevron-up</v-icon>
                </button>
                <button
                  v-if="index < form.tiebreakers.length - 1"
                  class="tiebreaker-btn"
                  @click="moveTiebreaker(index, 1)"
                >
                  <v-icon size="18">mdi-chevron-down</v-icon>
                </button>
              </div>
            </div>
          </div>
        </IosSection>

        <!-- Regular Season Groups -->
        <IosSection form :title="$t('seasonForm.groupsTitle')">
          <div v-for="(group, gIndex) in form.groups" :key="'rg-' + gIndex" class="group-section">
            <div class="group-header">
              <span class="group-label">{{ $t('seasonForm.group', { letter: getGroupLetter(gIndex) }) }}</span>
              <button 
                v-if="form.groups.length > 1" 
                class="group-remove-btn"
                @click="removeGroup(gIndex)"
              >
                <v-icon size="18" color="error">mdi-close</v-icon>
              </button>
            </div>

            <div v-if="group.teams.length > 0" class="group-teams">
              <div 
                v-for="teamId in group.teams" 
                :key="teamId" 
                class="group-team-chip"
              >
                <img 
                  :src="getTeamImageUrl(teamId)" 
                  class="team-chip-logo" 
                  alt=""
                />
                <span class="team-chip-name">{{ getTeamName(teamId) }}</span>
                <button class="team-chip-remove" @click="removeTeamFromGroup(gIndex, teamId)">
                  <v-icon size="14">mdi-close</v-icon>
                </button>
              </div>
            </div>
            <div v-else class="group-empty">
              {{ $t('seasonForm.noTeamsInGroup') }}
            </div>

            <IosAddRow
              :label="$t('seasonForm.addTeam')"
              last
              @click="openTeamPicker(gIndex)"
            />
          </div>

          <IosAddRow
            :label="$t('seasonForm.addGroup')"
            last
            @click="addGroup"
          />
        </IosSection>

        <IosSection form>
          <div v-if="isEditMode" class="generate-playoff-wrap">
            <button class="generate-playoff-btn" :disabled="isGeneratingRegular" @click="handleGenerateRegular">
              <v-icon size="18" class="mr-1">mdi-auto-fix</v-icon>
              {{ isGeneratingRegular ? $t('seasonForm.generating') : isFormDirty ? $t('seasonForm.saveAndGenerateRegular') : $t('seasonForm.generateRegular') }}
            </button>
            <div v-if="generateError" class="generate-error">{{ generateError }}</div>
            <div class="generate-playoff-hint">{{ $t('seasonForm.generateRegularHint') }}</div>
            <button class="generate-playoff-btn generate-playoff-btn--delete" @click="showDeleteAutogenConfirm = true">
              <v-icon size="18" class="mr-1">mdi-delete-outline</v-icon>
              {{ $t('seasonForm.deleteAutogenerated') }}
            </button>
          </div>
        </IosSection>

        </div><!-- /TAB: Základní část -->

        <!-- TAB: Playoff -->
        <div v-show="seasonTab === 'playoff'" class="sf-tab-content">
        <!-- Playoff / Vyřazovací část -->
        <IosSection form :title="$t('seasonForm.playoffTitle')">
          <!-- Scenario Type -->
          <IosRow 
            :label="$t('seasonForm.scenarioType')"
            :value="scenarioTypeLabel"
            clickable
            first
            @click="currentView = 'scenarioType'"
          />
        </IosSection>

        <!-- Section: Playoff groups -->
        <IosSection v-if="hasGroups(form.playoffConfig.scenarioType)" form :title="$t('seasonForm.playoffGroupsSectionTitle')">
          <IosRow :label="$t('seasonForm.groupCount')" first>
            <template #value>
              <input type="number" class="number-input" v-model.number="form.playoffConfig.groupCount" min="1" max="16" />
            </template>
          </IosRow>
          <IosRow :label="$t('seasonForm.teamsPerGroup')" :value="String(teamsPerGroup)" />
          <IosRow
            :label="$t('seasonForm.groupFormatLabel')"
            :value="groupFormatLabel"
            clickable
            @click="currentView = 'groupFormat'"
          />
          <IosRow
            :label="$t('seasonForm.groupAssignment')"
            :value="groupAssignmentLabel"
            clickable
            @click="currentView = 'groupAssignment'"
          />

          <!-- Bonus points -->
          <IosSwitch
            v-model="form.playoffConfig.bonusEnabled"
            :label="$t('seasonForm.bonusEnabled')"
            :subtitle="$t('seasonForm.bonusEnabledDesc')"
          />
          <template v-if="form.playoffConfig.bonusEnabled">
            <IosRow
              :label="$t('seasonForm.bonusMethod')"
              :value="form.playoffConfig.bonusMethod === 'linear' ? $t('seasonForm.bonusLinear') : $t('seasonForm.bonusFixed')"
              clickable
              @click="form.playoffConfig.bonusMethod = form.playoffConfig.bonusMethod === 'linear' ? 'fixed' : 'linear'"
            />
            <IosRow v-if="form.playoffConfig.bonusMethod === 'linear'" :label="$t('seasonForm.bonusTopValue')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.playoffConfig.bonusTopValue" min="1" max="20" />
              </template>
            </IosRow>
            <template v-if="form.playoffConfig.bonusMethod === 'fixed'">
              <IosRow
                v-for="row in form.playoffConfig.bonusTable"
                :key="row.position"
                :label="`${row.position}. ${$t('seasonForm.bonusPosition')}`"
              >
                <template #value>
                  <input type="number" class="number-input" v-model.number="row.points" min="0" max="99" />
                </template>
              </IosRow>
            </template>
          </template>

          <!-- Groups custom settings toggle -->
          <IosSwitch
            v-model="form.groupsCustomSettings"
            :label="$t('seasonForm.groupsCustomSettings')"
            :subtitle="$t('seasonForm.groupsCustomSettingsDesc')"
            :last="!form.groupsCustomSettings"
          />

          <template v-if="form.groupsCustomSettings">
            <!-- Groups Scoring Rules -->
            <div class="playoff-sub-header">{{ $t('seasonForm.groupsScoringSubtitle') }}</div>
            <IosSwitch 
              v-model="form.groupsSplitPointsByParts" 
              :label="$t('seasonForm.splitPoints')"
              :subtitle="$t('seasonForm.splitPointsDesc')"
            />
            <IosRow :label="$t('seasonForm.winPoints')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.groupsPointsWin" min="0" />
              </template>
            </IosRow>
            <IosRow :label="$t('seasonForm.overtimeWinPoints')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.groupsPointsOvertimeWin" min="0" />
              </template>
            </IosRow>
            <IosRow :label="$t('seasonForm.overtimeLossPoints')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.groupsPointsOvertimeLoss" min="0" />
              </template>
            </IosRow>

            <!-- Groups Game Settings -->
            <div class="playoff-sub-header">{{ $t('seasonForm.groupsGameSubtitle') }}</div>
            <IosRow :label="$t('seasonForm.periodsCount')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.groupsPartsCount" min="1" max="5" />
              </template>
            </IosRow>
            <IosRow :label="$t('seasonForm.periodLength')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.groupsPartTime" min="1" />
              </template>
            </IosRow>
            <IosRow 
              :label="$t('seasonForm.playersCount')"
              :value="form.groupsPlayersCount"
              clickable
              @click="currentView = 'groupsPlayersCount'"
            />
            <IosRow :label="$t('seasonForm.overtimeLength')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.groupsOvertimeDuration" min="0" :placeholder="$t('seasonForm.overtimePlaceholder')" />
              </template>
            </IosRow>
            <IosRow :label="$t('seasonForm.minorPenalty')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.groupsPenaltySmall" min="0" step="0.5" />
              </template>
            </IosRow>
            <IosRow :label="$t('seasonForm.majorPenalty')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.groupsPenaltyBig" min="0" />
              </template>
            </IosRow>
            <IosRow :label="$t('seasonForm.misconductPenalty')" last>
              <template #value>
                <input type="number" class="number-input" v-model.number="form.groupsPenaltyPersonal" min="0" />
              </template>
            </IosRow>
          </template>

          <!-- Playoff group teams -->
          <div v-if="isEditMode" class="generate-playoff-wrap" style="margin-top: 8px;">
            <button 
              class="generate-playoff-btn" 
              :disabled="isGeneratingGroups || !canGeneratePlayoffGroups" 
              @click="handleGeneratePlayoffGroups"
            >
              <v-icon size="18" class="mr-1">mdi-refresh</v-icon>
              {{ isGeneratingGroups ? $t('seasonForm.generating') : isFormDirty ? $t('seasonForm.saveAndGenerateGroups') : $t('seasonForm.generateGroups') }}
            </button>
            <div class="generate-playoff-hint">
              {{ canGeneratePlayoffGroups ? $t('seasonForm.generateGroupsHint') : $t('seasonForm.generateGroupsNeedTeams') }}
            </div>
          </div>

          <div v-for="(group, gIndex) in form.playoffGroups" :key="'pg-' + gIndex" class="group-section">
            <div class="group-header">
              <span class="group-label">{{ $t('seasonForm.group', { letter: getGroupLetter(gIndex) }) }}</span>
              <button 
                v-if="form.playoffGroups.length > 1" 
                class="group-remove-btn"
                @click="removePlayoffGroup(gIndex)"
              >
                <v-icon size="18" color="error">mdi-close</v-icon>
              </button>
            </div>

            <div v-if="group.teams.length > 0" class="group-teams">
              <div 
                v-for="entry in group.teams" 
                :key="entry.id" 
                class="group-team-chip"
              >
                <img 
                  :src="getTeamImageUrl(entry.id)" 
                  class="team-chip-logo" 
                  alt=""
                />
                <span class="team-chip-name">{{ getTeamName(entry.id) }}</span>
                <button class="team-chip-remove" @click="removeTeamFromPlayoffGroup(gIndex, entry.id)">
                  <v-icon size="14">mdi-close</v-icon>
                </button>
              </div>
            </div>
            <div v-else class="group-empty">
              {{ $t('seasonForm.noTeamsInGroup') }}
            </div>

            <IosAddRow
              :label="$t('seasonForm.addTeam')"
              last
              @click="openPlayoffTeamPicker(gIndex)"
            />
          </div>

          <IosAddRow
            :label="$t('seasonForm.addGroup')"
            last
            @click="addPlayoffGroup"
          />
        </IosSection>

        <!-- Section 4: Playoff branches -->
        <IosSection v-if="form.playoffConfig.scenarioType" form :title="$t('seasonForm.branchesTitle')">
          <!-- Playoff custom settings toggle -->
          <IosSwitch
            v-model="form.playoffCustomSettings"
            :label="$t('seasonForm.playoffCustomSettings')"
            :subtitle="$t('seasonForm.playoffCustomSettingsDesc')"
            first
          />

          <template v-if="form.playoffCustomSettings">
            <!-- Playoff Scoring Rules -->
            <div class="playoff-sub-header">{{ $t('seasonForm.playoffScoringSubtitle') }}</div>
            <IosSwitch 
              v-model="form.playoffSplitPointsByParts" 
              :label="$t('seasonForm.splitPoints')"
              :subtitle="$t('seasonForm.splitPointsDesc')"
            />
            <IosRow :label="$t('seasonForm.winPoints')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.playoffPointsWin" min="0" />
              </template>
            </IosRow>
            <IosRow :label="$t('seasonForm.overtimeWinPoints')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.playoffPointsOvertimeWin" min="0" />
              </template>
            </IosRow>
            <IosRow :label="$t('seasonForm.overtimeLossPoints')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.playoffPointsOvertimeLoss" min="0" />
              </template>
            </IosRow>

            <!-- Playoff Game Settings -->
            <div class="playoff-sub-header">{{ $t('seasonForm.playoffGameSubtitle') }}</div>
            <IosRow :label="$t('seasonForm.periodsCount')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.playoffPartsCount" min="1" max="5" />
              </template>
            </IosRow>
            <IosRow :label="$t('seasonForm.periodLength')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.playoffPartTime" min="1" />
              </template>
            </IosRow>
            <IosRow 
              :label="$t('seasonForm.playersCount')"
              :value="form.playoffPlayersCount"
              clickable
              @click="currentView = 'playoffPlayersCount'"
            />
            <IosRow :label="$t('seasonForm.overtimeLength')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.playoffOvertimeDuration" min="0" :placeholder="$t('seasonForm.overtimePlaceholder')" />
              </template>
            </IosRow>
            <IosRow :label="$t('seasonForm.minorPenalty')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.playoffPenaltySmall" min="0" step="0.5" />
              </template>
            </IosRow>
            <IosRow :label="$t('seasonForm.majorPenalty')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.playoffPenaltyBig" min="0" />
              </template>
            </IosRow>
            <IosRow :label="$t('seasonForm.misconductPenalty')">
              <template #value>
                <input type="number" class="number-input" v-model.number="form.playoffPenaltyPersonal" min="0" />
              </template>
            </IosRow>
          </template>

          <div v-for="branch in form.playoffConfig.branches" :key="branch.id" class="branch-card" :style="{ borderLeftColor: branchColorMap[branch.color] || 'var(--color-border-tertiary)' }">
            <div class="branch-card__header">
              <span class="badge" :class="branch.color">{{ branch.id }}</span>
              <input type="text" class="branch-name-input" v-model="branch.name" />
              <button class="adv-remove" @click="removeBranch(branch.id)">
                <v-icon size="16">mdi-close</v-icon>
              </button>
            </div>
            <div class="branch-card__fields">
              <div class="branch-field">
                <span class="branch-field__label">{{ $t('seasonForm.teamsFrom') }}</span>
                <select class="adv-select" v-model="branch.teamsFrom">
                  <option value="adv_1">{{ $t('seasonForm.teamsFromAdv1') }}</option>
                  <option value="adv_2">{{ $t('seasonForm.teamsFromAdv2') }}</option>
                  <option value="prelim_losers_b1">{{ $t('seasonForm.teamsFromPrelimLosersB1') }}</option>
                  <option value="prelim_losers_b2">{{ $t('seasonForm.teamsFromPrelimLosersB2') }}</option>
                  <option value="qf_losers_b1">{{ $t('seasonForm.teamsFromQfLosersB1') }}</option>
                  <option value="qf_losers_b2">{{ $t('seasonForm.teamsFromQfLosersB2') }}</option>
                  <option value="sf_losers_b1">{{ $t('seasonForm.teamsFromSfLosersB1') }}</option>
                  <option value="sf_losers_b2">{{ $t('seasonForm.teamsFromSfLosersB2') }}</option>
                  <option value="custom">{{ $t('seasonForm.teamsFromCustom') }}</option>
                </select>
              </div>
              <div class="branch-field">
                <span class="branch-field__label">{{ $t('seasonForm.branchTeamCount') }}</span>
                <input type="number" class="number-input" v-model.number="branch.teamCount" min="2" max="32" />
              </div>
              <div class="branch-field">
                <span class="branch-field__label">{{ $t('seasonForm.preliminaryRounds') }}</span>
                <input type="number" class="number-input" v-model.number="branch.preliminaryRounds" min="0" max="8" />
              </div>
              <div class="branch-field">
                <span class="branch-field__label">{{ $t('seasonForm.branchColor') }}</span>
                <select class="adv-select" v-model="branch.color">
                  <option v-for="c in branchColorOptions" :key="c.value" :value="c.value">{{ $t(c.label) }}</option>
                </select>
              </div>
            </div>

          </div>
          <IosAddRow :label="$t('seasonForm.addBranch')" @click="addBranch" />

          <!-- Bracket preview for all playoff scenarios -->
          <template v-if="form.playoffConfig.scenarioType && form.playoffConfig.branches.length > 0">
            <div class="playoff-sub-header">{{ $t('advRules.bracketPreview') }}</div>
            <PlayoffBracketPreview
              :rules="[]"
              :branches="previewBranches"
              :groupCount="form.playoffConfig.groupCount"
              :placementMatches="previewPlacementMatches"
              @update:series="onBracketSeriesUpdate"
            />
          </template>
        </IosSection>

        <!-- Section 5: Placement matches -->
        <IosSection v-if="form.playoffConfig.scenarioType && form.playoffConfig.branches.length > 0" form :title="$t('seasonForm.placementMatchesTitle')">
          <div v-for="(pm, pi) in form.playoffConfig.placementMatches" :key="pi" class="pm-row">
            <input type="text" class="pm-name-input" v-model="pm.name" />
            <div class="pm-field">
              <span class="pm-field__label">{{ $t('seasonForm.placeSeries') }}:</span>
              <select class="adv-select pm-series-select" v-model.number="pm.series">
                <option :value="1">{{ $t('seasonForm.placeSeries1') }}</option>
                <option :value="2">{{ $t('seasonForm.placeSeries2') }}</option>
                <option :value="3">{{ $t('seasonForm.placeSeries3') }}</option>
              </select>
            </div>
            <div class="pm-field">
              <span class="pm-field__label">{{ $t('seasonForm.placeTeamsFrom') }}:</span>
              <select class="adv-select" v-model="pm.teamsFrom">
                <template v-for="b in form.playoffConfig.branches" :key="'pm-opts-' + b.id">
                  <option :value="'branch_' + b.id + '_sf_losers'">{{ $t('seasonForm.sfLosersOf', { name: b.name }) }}</option>
                </template>
              </select>
            </div>
            <button class="adv-remove" @click="removePlacementMatch(pi)">
              <v-icon size="16">mdi-close</v-icon>
            </button>
          </div>
          <IosAddRow :label="$t('seasonForm.addPlacementMatch')" @click="addPlacementMatch" />
        </IosSection>

        <!-- Consolation group (available for all scenarios) -->
        <IosSection v-if="form.playoffConfig.scenarioType" form :title="$t('seasonForm.consolationGroupTitle')">
          <IosSwitch v-model="form.playoffConfig.consolationGroupEnabled" :label="$t('seasonForm.consolationGroupEnabled')" first :last="!form.playoffConfig.consolationGroupEnabled" />
          <template v-if="form.playoffConfig.consolationGroupEnabled">
            <IosRow
              :label="$t('seasonForm.consolationGroupFrom')"
              :value="consolationGroupFromLabel"
              clickable
              @click="currentView = 'consolationGroupFrom'"
            />
            <IosRow :label="$t('seasonForm.consolationGroupNameLabel')">
              <template #value>
                <input type="text" class="number-input" v-model="form.playoffConfig.consolationGroupName" style="width:140px" />
              </template>
            </IosRow>
            <IosRow
              :label="$t('seasonForm.consolationGroupRoundsLabel')"
            >
              <template #value>
                <input type="number" class="number-input" v-model.number="form.playoffConfig.consolationGroupRounds" min="1" max="10" />
              </template>
            </IosRow>

            <!-- Consolation custom settings toggle -->
            <IosSwitch
              v-model="form.consolationCustomSettings"
              :label="$t('seasonForm.consolationCustomSettings')"
              :subtitle="$t('seasonForm.consolationCustomSettingsDesc')"
              :last="!form.consolationCustomSettings"
            />

            <template v-if="form.consolationCustomSettings">
              <!-- Consolation Scoring Rules -->
              <div class="playoff-sub-header">{{ $t('seasonForm.consolationScoringSubtitle') }}</div>
              <IosSwitch 
                v-model="form.consolationSplitPointsByParts" 
                :label="$t('seasonForm.splitPoints')"
                :subtitle="$t('seasonForm.splitPointsDesc')"
              />
              <IosRow :label="$t('seasonForm.winPoints')">
                <template #value>
                  <input type="number" class="number-input" v-model.number="form.consolationPointsWin" min="0" />
                </template>
              </IosRow>
              <IosRow :label="$t('seasonForm.overtimeWinPoints')">
                <template #value>
                  <input type="number" class="number-input" v-model.number="form.consolationPointsOvertimeWin" min="0" />
                </template>
              </IosRow>
              <IosRow :label="$t('seasonForm.overtimeLossPoints')">
                <template #value>
                  <input type="number" class="number-input" v-model.number="form.consolationPointsOvertimeLoss" min="0" />
                </template>
              </IosRow>

              <!-- Consolation Game Settings -->
              <div class="playoff-sub-header">{{ $t('seasonForm.consolationGameSubtitle') }}</div>
              <IosRow :label="$t('seasonForm.periodsCount')">
                <template #value>
                  <input type="number" class="number-input" v-model.number="form.consolationPartsCount" min="1" max="5" />
                </template>
              </IosRow>
              <IosRow :label="$t('seasonForm.periodLength')">
                <template #value>
                  <input type="number" class="number-input" v-model.number="form.consolationPartTime" min="1" />
                </template>
              </IosRow>
              <IosRow 
                :label="$t('seasonForm.playersCount')"
                :value="form.consolationPlayersCount"
                clickable
                @click="currentView = 'consolationPlayersCount'"
              />
              <IosRow :label="$t('seasonForm.overtimeLength')">
                <template #value>
                  <input type="number" class="number-input" v-model.number="form.consolationOvertimeDuration" min="0" :placeholder="$t('seasonForm.overtimePlaceholder')" />
                </template>
              </IosRow>
              <IosRow :label="$t('seasonForm.minorPenalty')">
                <template #value>
                  <input type="number" class="number-input" v-model.number="form.consolationPenaltySmall" min="0" step="0.5" />
                </template>
              </IosRow>
              <IosRow :label="$t('seasonForm.majorPenalty')">
                <template #value>
                  <input type="number" class="number-input" v-model.number="form.consolationPenaltyBig" min="0" />
                </template>
              </IosRow>
              <IosRow :label="$t('seasonForm.misconductPenalty')" last>
                <template #value>
                  <input type="number" class="number-input" v-model.number="form.consolationPenaltyPersonal" min="0" />
                </template>
              </IosRow>
            </template>
          </template>
        </IosSection>

        <!-- Generate + Delete (always at bottom) -->
        <IosSection v-if="form.playoffConfig.scenarioType" form>
          <div v-if="isEditMode" class="generate-playoff-wrap">
            <button class="generate-playoff-btn" :disabled="isGenerating" @click="handleGeneratePlayoff">
              <v-icon size="18" class="mr-1">mdi-auto-fix</v-icon>
              {{ isGenerating ? $t('seasonForm.generating') : isFormDirty ? $t('seasonForm.saveAndGeneratePlayoff') : $t('seasonForm.generatePlayoff') }}
            </button>
            <div class="generate-playoff-hint">{{ $t('seasonForm.generatePlayoffHint') }}</div>
            <button class="generate-playoff-btn generate-playoff-btn--delete" @click="showDeletePlayoffAutogenConfirm = true">
              <v-icon size="18" class="mr-1">mdi-delete-outline</v-icon>
              {{ $t('seasonForm.deletePlayoffAutogenerated') }}
            </button>
          </div>
        </IosSection>

        </div><!-- /TAB: Playoff -->

      </div>

      <!-- Players Count Select View -->
      <Transition name="slide">
        <div v-if="currentView === 'playersCount'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('seasonForm.playersCount') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form>
              <IosRow 
                v-for="(option, index) in playersCountOptions" 
                :key="option"
                :label="option"
                clickable
                :first="index === 0"
                :last="index === playersCountOptions.length - 1"
                @click="form.playersCount = option; currentView = 'main'"
              >
                <template #value>
                  <v-icon v-if="form.playersCount === option" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Playoff Players Count Select View -->
      <Transition name="slide">
        <div v-if="currentView === 'playoffPlayersCount'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('seasonForm.playersCount') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form>
              <IosRow 
                v-for="(option, index) in playersCountOptions" 
                :key="option"
                :label="option"
                clickable
                :first="index === 0"
                :last="index === playersCountOptions.length - 1"
                @click="form.playoffPlayersCount = option; currentView = 'main'"
              >
                <template #value>
                  <v-icon v-if="form.playoffPlayersCount === option" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Consolation Players Count Select View -->
      <Transition name="slide">
        <div v-if="currentView === 'consolationPlayersCount'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('seasonForm.playersCount') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form>
              <IosRow 
                v-for="(option, index) in playersCountOptions" 
                :key="option"
                :label="option"
                clickable
                :first="index === 0"
                :last="index === playersCountOptions.length - 1"
                @click="form.consolationPlayersCount = option; currentView = 'main'"
              >
                <template #value>
                  <v-icon v-if="form.consolationPlayersCount === option" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Groups Players Count Select View -->
      <Transition name="slide">
        <div v-if="currentView === 'groupsPlayersCount'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('seasonForm.playersCount') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form>
              <IosRow 
                v-for="(option, index) in playersCountOptions" 
                :key="option"
                :label="option"
                clickable
                :first="index === 0"
                :last="index === playersCountOptions.length - 1"
                @click="form.groupsPlayersCount = option; currentView = 'main'"
              >
                <template #value>
                  <v-icon v-if="form.groupsPlayersCount === option" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Team Picker View -->
      <Transition name="slide">
        <div v-if="currentView === 'teamPicker'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('seasonForm.selectTeamGroup', { letter: getGroupLetter(currentGroupIndex) }) }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form v-if="availableTeamsForGroup.length > 0">
              <IosRow 
                v-for="(team, index) in availableTeamsForGroup" 
                :key="team._id"
                :label="team.name"
                clickable
                :first="index === 0"
                :last="index === availableTeamsForGroup.length - 1"
                @click="addTeamToGroup(team._id)"
              >
                <template #value>
                  <img 
                    :src="getTeamImage(team.imageUrl)" 
                    class="team-pick-logo" 
                    alt=""
                  />
                </template>
              </IosRow>
            </IosSection>
            <div v-else class="search-empty">
              {{ $t('seasonForm.noAvailableTeams') }}
            </div>
          </div>
        </div>
      </Transition>

      <!-- Scenario Type Picker View -->
      <Transition name="slide">
        <div v-if="currentView === 'scenarioType'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('seasonForm.scenarioType') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form>
              <IosRow 
                v-for="(option, index) in scenarioTypeOptions" 
                :key="option.value"
                :label="$t(option.label)"
                :subtitle="$t(option.desc)"
                clickable
                :first="index === 0"
                :last="index === scenarioTypeOptions.length - 1"
                @click="onScenarioTypeSelect(option.value); currentView = 'main'"
              >
                <template #value>
                  <v-icon v-if="form.playoffConfig.scenarioType === option.value" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Season Type Picker View -->
      <Transition name="slide">
        <div v-if="currentView === 'seasonType'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('seasonForm.seasonTypeTitle') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form>
              <IosRow 
                v-for="(option, index) in seasonTypeOptions" 
                :key="option.value"
                :label="$t(option.label)"
                clickable
                :first="index === 0"
                :last="index === seasonTypeOptions.length - 1"
                @click="form.seasonType = option.value; currentView = 'main'"
              >
                <template #value>
                  <v-icon v-if="form.seasonType === option.value" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Group Format Picker View -->
      <Transition name="slide">
        <div v-if="currentView === 'groupFormat'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('seasonForm.groupFormatLabel') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow v-for="(opt, index) in groupFormatOptions" :key="opt.value"
                :label="$t(opt.label)" :subtitle="$t(opt.desc)" clickable
                :first="index === 0" :last="index === groupFormatOptions.length - 1"
                @click="form.playoffConfig.groupFormat = opt.value; currentView = 'main'"
              >
                <template #value><v-icon v-if="form.playoffConfig.groupFormat === opt.value" size="20" color="success">mdi-check</v-icon></template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Group Assignment Picker View -->
      <Transition name="slide">
        <div v-if="currentView === 'groupAssignment'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('seasonForm.groupAssignment') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow v-for="(opt, index) in groupAssignmentOptions" :key="opt.value"
                :label="$t(opt.label)" :subtitle="$t(opt.desc)" clickable
                :first="index === 0" :last="index === groupAssignmentOptions.length - 1"
                @click="form.playoffConfig.groupAssignment = opt.value; currentView = 'main'"
              >
                <template #value><v-icon v-if="form.playoffConfig.groupAssignment === opt.value" size="20" color="success">mdi-check</v-icon></template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Consolation Group From Picker View -->
      <Transition name="slide">
        <div v-if="currentView === 'consolationGroupFrom'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('seasonForm.consolationGroupFrom') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow v-for="(opt, index) in consolationGroupFromOptions" :key="opt.value"
                :label="$t(opt.label)" clickable
                :first="index === 0" :last="index === consolationGroupFromOptions.length - 1"
                @click="form.playoffConfig.consolationGroupFrom = opt.value; currentView = 'main'"
              >
                <template #value><v-icon v-if="form.playoffConfig.consolationGroupFrom === opt.value" size="20" color="success">mdi-check</v-icon></template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Playoff Team Picker View -->
      <Transition name="slide">
        <div v-if="currentView === 'playoffTeamPicker'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('seasonForm.selectTeamGroup', { letter: getGroupLetter(currentPlayoffGroupIndex) }) }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form v-if="availableTeamsForPlayoffGroup.length > 0">
              <IosRow 
                v-for="(team, index) in availableTeamsForPlayoffGroup" 
                :key="team._id"
                :label="team.name"
                clickable
                :first="index === 0"
                :last="index === availableTeamsForPlayoffGroup.length - 1"
                @click="addTeamToPlayoffGroup(team._id)"
              >
                <template #value>
                  <img 
                    :src="getTeamImage(team.imageUrl)" 
                    class="team-pick-logo" 
                    alt=""
                  />
                </template>
              </IosRow>
            </IosSection>
            <div v-else class="search-empty">
              {{ $t('seasonForm.noAvailableTeams') }}
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </BottomSheetModal>

  <!-- Confirm deactivation of playoff -->
  <CenteredModal v-model="showPlayoffDeactivateConfirm" :showCloseButton="false" maxWidth="360px" :closeOnOverlay="false" :zIndex="10001">
    <div class="deactivate-confirm">
      <v-icon size="48" color="warning" class="deactivate-confirm__icon">mdi-alert-circle-outline</v-icon>
      <h3 class="deactivate-confirm__title">{{ $t('seasonForm.playoffDeactivateTitle') }}</h3>
      <p class="deactivate-confirm__text">{{ $t('seasonForm.playoffDeactivateText') }}</p>
      <div class="deactivate-confirm__actions">
        <button class="deactivate-confirm__btn deactivate-confirm__btn--cancel" @click="showPlayoffDeactivateConfirm = false">{{ $t('cancel') }}</button>
        <button class="deactivate-confirm__btn deactivate-confirm__btn--confirm" @click="confirmPlayoffDeactivate">{{ $t('seasonForm.playoffDeactivateConfirm') }}</button>
      </div>
    </div>
  </CenteredModal>

  <!-- Confirm delete autogenerated events -->
  <CenteredModal v-model="showDeleteAutogenConfirm" :showCloseButton="true" maxWidth="350px" :zIndex="10001">
    <div class="delete-confirm-dialog">
      <div class="delete-confirm-icon">
        <v-icon size="48" color="error">mdi-delete-alert</v-icon>
      </div>
      <h3 class="delete-confirm-title">{{ $t('seasonForm.deleteAutogenerated') }}</h3>
      <p class="delete-confirm-subtitle">{{ $t('seasonForm.deleteAutogeneratedConfirm') }}</p>
      <div class="delete-confirm-input-wrap">
        <input
          v-model="deleteAutogenInput"
          class="delete-confirm-input"
          :placeholder="$t('typeToConfirmDelete', { word: $t('delete').toLowerCase() })"
          autocomplete="off"
        />
      </div>
      <div class="delete-confirm-actions">
        <button class="delete-confirm-btn delete-confirm-btn--cancel" @click="cancelDeleteAutogen">
          {{ $t('cancel') }}
        </button>
        <button
          class="delete-confirm-btn delete-confirm-btn--confirm"
          :disabled="!isDeleteAutogenValid"
          @click="executeDeleteAutogen"
        >
          {{ $t('delete') }}
        </button>
      </div>
    </div>
  </CenteredModal>

  <!-- Confirm delete autogenerated playoff events -->
  <CenteredModal v-model="showDeletePlayoffAutogenConfirm" :showCloseButton="true" maxWidth="350px" :zIndex="10001">
    <div class="delete-confirm-dialog">
      <div class="delete-confirm-icon">
        <v-icon size="48" color="error">mdi-delete-alert</v-icon>
      </div>
      <h3 class="delete-confirm-title">{{ $t('seasonForm.deletePlayoffAutogenerated') }}</h3>
      <p class="delete-confirm-subtitle">{{ $t('seasonForm.deletePlayoffAutogeneratedConfirm') }}</p>
      <div class="delete-confirm-input-wrap">
        <input
          v-model="deletePlayoffAutogenInput"
          class="delete-confirm-input"
          :placeholder="$t('typeToConfirmDelete', { word: $t('delete').toLowerCase() })"
          autocomplete="off"
        />
      </div>
      <div class="delete-confirm-actions">
        <button class="delete-confirm-btn delete-confirm-btn--cancel" @click="cancelDeletePlayoffAutogen">
          {{ $t('cancel') }}
        </button>
        <button
          class="delete-confirm-btn delete-confirm-btn--confirm"
          :disabled="!isDeletePlayoffAutogenValid || deletePlayoffAutogenLoading"
          @click="executeDeletePlayoffAutogen"
        >
          <v-progress-circular v-if="deletePlayoffAutogenLoading" size="16" width="2" indeterminate class="mr-1" />
          {{ $t('delete') }}
        </button>
      </div>
    </div>
  </CenteredModal>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, provide, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/app/appStore';
import BottomSheetModal from '@/components/BottomSheetModal.vue';
import CenteredModal from '@/components/CenteredModal.vue';
import { IosSection, IosRow, IosSwitch, IosAddRow } from '@/components/ios';
import PlayoffBracketPreview from '@/components/league/PlayoffBracketPreview.vue';
import { autoRounds } from '@/composables/usePlayoffBracket';
import type { AdvancementRule, PlayoffBranch as PlayoffBranchType } from '@/types/playoff';
import type { ISeason } from '@/types';
import axiosInstance from '@/services/axiosInstance';
import { getTeamImage } from '@/utils/imageUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

// ── Playoff deactivation confirmation ──
const showPlayoffDeactivateConfirm = ref(false);

const onPlayoffActiveToggle = (val: boolean) => {
  if (!val && form.value.playoffActive && isEditMode.value && props.season?.playoffActive) {
    // Turning off while it was saved as active → confirm
    showPlayoffDeactivateConfirm.value = true;
  } else {
    form.value.playoffActive = val;
  }
};

const confirmPlayoffDeactivate = () => {
  form.value.playoffActive = false;
  showPlayoffDeactivateConfirm.value = false;
};
const { t } = useI18n({ useScope: 'global' })

type ViewType = 'main' | 'playersCount' | 'playoffPlayersCount' | 'consolationPlayersCount' | 'groupsPlayersCount' | 'consolationGroupFrom' | 'teamPicker' | 'scenarioType' | 'seasonType' | 'playoffTeamPicker' | 'groupAssignment' | 'groupFormat';

const playersCountOptions = ['5+1', '3+1'];

const seasonTypeOptions = [
  { value: 'league' as const, label: 'seasonForm.seasonTypeLeague' },
  { value: 'tournament' as const, label: 'seasonForm.seasonTypeTournament' },
];

const scenarioTypeOptions = [
  { value: 'groups_playoff', label: 'seasonForm.groupsPlayoff', desc: 'seasonForm.groupsPlayoffDesc' },
  { value: 'direct_playoff', label: 'seasonForm.directPlayoff', desc: 'seasonForm.directPlayoffDesc' },
  { value: 'prelim_groups_playoff', label: 'seasonForm.prelimGroupsPlayoff', desc: 'seasonForm.prelimGroupsPlayoffDesc' },
  { value: 'mh_groups_playoff', label: 'seasonForm.mhGroupsPlayoff', desc: 'seasonForm.mhGroupsPlayoffDesc' },
] as const;

const groupScoringOptions = [
  { value: '2-1-0' as const, label: 'seasonForm.scoring210' },
  { value: '3-2-1-0' as const, label: 'seasonForm.scoring3210' },
  { value: '3-1-0' as const, label: 'seasonForm.scoring310' },
];

const groupFormatOptions = [
  { value: 'rr1' as const, label: 'seasonForm.rr1', desc: 'seasonForm.rr1Desc' },
  { value: 'rr2' as const, label: 'seasonForm.rr2', desc: 'seasonForm.rr2Desc' },
  { value: 'mh' as const, label: 'seasonForm.mh', desc: 'seasonForm.mhDesc' },
] as const;

const groupAssignmentOptions = [
  { value: 'snake' as const, label: 'seasonForm.assignSnake', desc: 'seasonForm.assignSnakeDesc' },
  { value: 'mh_snake' as const, label: 'seasonForm.assignMhSnake', desc: 'seasonForm.assignMhSnakeDesc' },
  { value: 'draw' as const, label: 'seasonForm.assignDraw', desc: 'seasonForm.assignDrawDesc' },
  { value: 'fixed' as const, label: 'seasonForm.assignFixed', desc: 'seasonForm.assignFixedDesc' },
];

const branchColorOptions = [
  { value: 'bg', label: 'seasonForm.colorGreen' },
  { value: 'ba', label: 'seasonForm.colorOrange' },
  { value: 'bt', label: 'seasonForm.colorTeal' },
  { value: 'bpk', label: 'seasonForm.colorPink' },
  { value: 'bgr', label: 'seasonForm.colorGray' },
] as const;

const branchColorMap: Record<string, string> = {
  bg: '#EAF3DE', ba: '#FAEEDA', bt: '#E1F5EE', bpk: '#FBEAF0', bgr: 'var(--color-background-secondary)',
};

type ScenarioType = '' | 'groups_playoff' | 'direct_playoff' | 'prelim_groups_playoff' | 'mh_groups_playoff';

interface OldAdvancementRule {
  positions: string;
  playoffBranch: string;
  description: string;
}

interface BranchRound {
  name: string;
  series: number;
}

interface PlayoffBranch {
  id: number;
  name: string;
  color: string;
  teamsFrom: string;
  teamCount: number;
  preliminaryRounds: number;
  preliminarySeries: number;
  rounds: BranchRound[];
}

interface PlacementMatch {
  place: number;
  name: string;
  teamsFrom: string;
  series: number;
}

interface PlayoffConfig {
  scenarioType: ScenarioType;
  // Regular season groups
  groupCount: number;
  groupFormat: 'rr1' | 'rr2' | 'mh';
  groupScoring: '2-1-0' | '3-2-1-0' | '3-1-0';
  groupAssignment: 'snake' | 'mh_snake' | 'draw' | 'fixed';
  tiebreakMiniTable: boolean;
  // Advancement (legacy)
  advancement: OldAdvancementRule[];
  // Advanced advancement rules
  advancementRules: AdvancementRule[];
  // Consolation group in regular season
  consolationGroupEnabled: boolean;
  consolationGroupFrom: string;
  consolationGroupName: string;
  consolationGroupRounds: number;
  // Bonus points
  bonusEnabled: boolean;
  bonusMethod: 'linear' | 'fixed';
  bonusTopValue: number;
  bonusTable: { position: number; points: number }[];
  // Playoff branches
  branches: PlayoffBranch[];
  // Placement matches
  placementMatches: PlacementMatch[];
}

interface SeasonForm {
  seasonType: 'league' | 'tournament';
  dateFrom: string;
  dateTo: string;
  active: boolean;
  partsCount: number;
  partTime: number;
  playersCount: string;
  overtimeDuration: number;
  penaltySmall: number;
  penaltyBig: number;
  penaltyPersonal: number;
  registrationFeePlayer: number;
  registrationFeeGoalie: number;
  registrationFeeTeam: number;
  splitPointsByParts: boolean;
  pointsWin: number;
  pointsOvertimeWin: number;
  pointsOvertimeLoss: number;
  regularRounds: number;
  tiebreakers: string[];
  groups: { teams: string[] }[];
  content: string;
  _league: string;
  playoffFormat: string;
  playoffBestOf: number;
  preRound: boolean;
  playoffGroupsActive: boolean;
  playoffActive: boolean;
  playoffGroups: { teams: { id: string; bonusPoints: number }[] }[];
  playoffCustomSettings: boolean;
  playoffPartsCount: number;
  playoffPartTime: number;
  playoffPlayersCount: string;
  playoffOvertimeDuration: number;
  playoffPenaltySmall: number;
  playoffPenaltyBig: number;
  playoffPenaltyPersonal: number;
  playoffSplitPointsByParts: boolean;
  playoffPointsWin: number;
  playoffPointsOvertimeWin: number;
  playoffPointsOvertimeLoss: number;
  consolationCustomSettings: boolean;
  consolationPartsCount: number;
  consolationPartTime: number;
  consolationPlayersCount: string;
  consolationOvertimeDuration: number;
  consolationPenaltySmall: number;
  consolationPenaltyBig: number;
  consolationPenaltyPersonal: number;
  consolationSplitPointsByParts: boolean;
  consolationPointsWin: number;
  consolationPointsOvertimeWin: number;
  consolationPointsOvertimeLoss: number;
  groupsCustomSettings: boolean;
  groupsPartsCount: number;
  groupsPartTime: number;
  groupsPlayersCount: string;
  groupsOvertimeDuration: number;
  groupsPenaltySmall: number;
  groupsPenaltyBig: number;
  groupsPenaltyPersonal: number;
  groupsSplitPointsByParts: boolean;
  groupsPointsWin: number;
  groupsPointsOvertimeWin: number;
  groupsPointsOvertimeLoss: number;
  playoffConfig: PlayoffConfig;
}

const hasGroups = (st: ScenarioType) => st === 'groups_playoff' || st === 'prelim_groups_playoff' || st === 'mh_groups_playoff';

const props = withDefaults(defineProps<{
  modelValue: boolean;
  leagueId: string;
  season?: ISeason | null;
  localMode?: boolean;
}>(), {
  localMode: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'saved', season: any): void;
}>();

// Provide leagueHex fallback for PlayoffBracketPreview
provide('leagueHex', ref('#4caf50'));

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const isEditMode = computed(() => !!(props.season?._id || props.season?._localId));
const currentView = ref<ViewType>('main');
const currentGroupIndex = ref(0);

// ── Season form tabs ──
type SeasonTab = 'general' | 'regular' | 'playoff';
const seasonTab = ref<SeasonTab>('general');
const seasonTabs: { key: SeasonTab; label: string }[] = [
  { key: 'general', label: 'seasonForm.tabGeneral' },
  { key: 'regular', label: 'seasonForm.tabRegular' },
  { key: 'playoff', label: 'seasonForm.tabPlayoff' },
];
const seasonTabsRef = ref<HTMLElement | null>(null);
const seasonTabRefs = reactive<Record<string, HTMLElement>>({});
const seasonTabIndicatorStyle = computed(() => {
  const el = seasonTabRefs[seasonTab.value];
  const parent = seasonTabsRef.value;
  if (!el || !parent) return { opacity: '0' };
  return {
    width: `${el.offsetWidth}px`,
    transform: `translateX(${el.offsetLeft}px)`,
    opacity: '1',
  };
});
const isSaving = ref(false);

// Teams data
const teams = ref<{ _id: string; name: string; imageUrl?: string }[]>([]);

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultForm = (): SeasonForm => ({
  seasonType: 'league',
  dateFrom: formatDate(new Date()),
  dateTo: formatDate(new Date()),
  active: true,
  partsCount: 3,
  partTime: 15,
  playersCount: '5+1',
  overtimeDuration: 10,
  penaltySmall: 1.5,
  penaltyBig: 3,
  penaltyPersonal: 7,
  registrationFeePlayer: 0,
  registrationFeeGoalie: 0,
  registrationFeeTeam: 0,
  splitPointsByParts: false,
  pointsWin: 3,
  pointsOvertimeWin: 2,
  pointsOvertimeLoss: 1,
  regularRounds: 1,
  tiebreakers: ['h2h_points', 'h2h_goals', 'goal_diff', 'goals_for', 'lot'],
  groups: [{ teams: [] }],
  content: '',
  _league: props.leagueId,
  playoffFormat: '',
  playoffBestOf: 5,
  preRound: false,
  playoffGroupsActive: false,
  playoffActive: false,
  playoffGroups: [{ teams: [] as { id: string; bonusPoints: number }[] }],
  playoffCustomSettings: false,
  playoffPartsCount: 3,
  playoffPartTime: 15,
  playoffPlayersCount: '5+1',
  playoffOvertimeDuration: 10,
  playoffPenaltySmall: 1.5,
  playoffPenaltyBig: 3,
  playoffPenaltyPersonal: 7,
  playoffSplitPointsByParts: false,
  playoffPointsWin: 3,
  playoffPointsOvertimeWin: 2,
  playoffPointsOvertimeLoss: 1,
  consolationCustomSettings: false,
  consolationPartsCount: 3,
  consolationPartTime: 15,
  consolationPlayersCount: '5+1',
  consolationOvertimeDuration: 10,
  consolationPenaltySmall: 1.5,
  consolationPenaltyBig: 3,
  consolationPenaltyPersonal: 7,
  consolationSplitPointsByParts: false,
  consolationPointsWin: 3,
  consolationPointsOvertimeWin: 2,
  consolationPointsOvertimeLoss: 1,
  groupsCustomSettings: false,
  groupsPartsCount: 3,
  groupsPartTime: 15,
  groupsPlayersCount: '5+1',
  groupsOvertimeDuration: 10,
  groupsPenaltySmall: 1.5,
  groupsPenaltyBig: 3,
  groupsPenaltyPersonal: 7,
  groupsSplitPointsByParts: false,
  groupsPointsWin: 3,
  groupsPointsOvertimeWin: 2,
  groupsPointsOvertimeLoss: 1,
  playoffConfig: {
    scenarioType: '',
    groupCount: 2,
    groupFormat: 'rr2',
    groupScoring: '3-2-1-0',
    groupAssignment: 'snake',
    tiebreakMiniTable: true,
    advancement: [],
    advancementRules: [],
    consolationGroupEnabled: false,
    consolationGroupFrom: 'all_non_advancing',
    consolationGroupName: t('seasonForm.consolationGroupNameDefault'),
    consolationGroupRounds: 1,
    bonusEnabled: false,
    bonusMethod: 'linear',
    bonusTopValue: 8,
    bonusTable: [],
    branches: [],
    placementMatches: [],
  },
});

const form = ref<SeasonForm>(getDefaultForm());
const formSnapshot = ref('');
const isPopulating = ref(false);

const isFormDirty = computed(() => {
  return JSON.stringify(form.value) !== formSnapshot.value;
});

// Validation
const isFormValid = computed(() => {
  return form.value.dateFrom && form.value.dateTo && form.value.dateFrom <= form.value.dateTo;
});

// Fetch teams
const fetchTeams = async () => {
  try {
    const response = await axiosInstance.get('/teams');
    teams.value = response.data.map((team: any) => ({
      _id: team._id,
      name: team.name,
      imageUrl: team?.imageUrl,
    }));
  } catch (error) {
    console.error('Failed to load teams:', error);
  }
};

// Team helpers
const getTeamName = (teamId: string) => {
  return teams.value.find(tm => tm._id === teamId)?.name || t('seasonForm.unknownTeam');
};

const getTeamImageUrl = (teamId: string) => {
  const team = teams.value.find(tm => tm._id === teamId);
  return getTeamImage(team?.imageUrl || '');
};

// Groups
const getGroupLetter = (index: number): string => {
  return String.fromCharCode(65 + index);
};

const addGroup = () => {
  form.value.groups.push({ teams: [] });
};

const removeGroup = (index: number) => {
  form.value.groups.splice(index, 1);
};

const openTeamPicker = (groupIndex: number) => {
  currentGroupIndex.value = groupIndex;
  currentView.value = 'teamPicker';
};

// Playoff groups
const currentPlayoffGroupIndex = ref(0);

const hasPlayoffGroups = computed(() => {
  return hasGroups(form.value.playoffConfig.scenarioType);
});

const seasonTypeLabel = computed(() => {
  const opt = seasonTypeOptions.find(o => o.value === form.value.seasonType);
  return opt ? t(opt.label) : '';
});

const scenarioTypeLabel = computed(() => {
  const opt = scenarioTypeOptions.find(o => o.value === form.value.playoffConfig.scenarioType);
  return opt ? t(opt.label) : t('seasonForm.selectScenario');
});

const groupScoringLabel = computed(() => {
  const opt = groupScoringOptions.find(o => o.value === form.value.playoffConfig.groupScoring);
  return opt ? t(opt.label) : '';
});

const groupFormatLabel = computed(() => {
  const opt = groupFormatOptions.find(o => o.value === form.value.playoffConfig.groupFormat);
  return opt ? t(opt.label) : '';
});

const groupAssignmentLabel = computed(() => {
  const opt = groupAssignmentOptions.find(o => o.value === form.value.playoffConfig.groupAssignment);
  return opt ? t(opt.label) : '';
});

const consolationGroupFromOptions = [
  { value: 'all_non_advancing' as const, label: 'seasonForm.consolationFromAllNonAdvancing' },
] as const;

const consolationGroupFromLabel = computed(() => {
  const opt = consolationGroupFromOptions.find(o => o.value === form.value.playoffConfig.consolationGroupFrom);
  return opt ? t(opt.label) : form.value.playoffConfig.consolationGroupFrom;
});

const totalTeamCount = computed(() => {
  return form.value.groups.reduce((sum, g) => sum + g.teams.length, 0) || 16;
});

const teamsPerGroup = computed(() => {
  const gc = form.value.playoffConfig.groupCount || 1;
  return Math.round(totalTeamCount.value / gc);
});

const playoffMatchRange = computed(() => {
  let min = 0;
  let max = 0;
  for (const branch of form.value.playoffConfig.branches) {
    // Preliminary matches
    const prelimCount = branch.preliminaryRounds;
    const ps = branch.preliminarySeries;
    min += prelimCount * (ps === 1 ? 1 : ps);
    max += prelimCount * (ps === 1 ? 1 : 2 * ps - 1);
    // Main rounds
    const teamsAfterPrelim = branch.teamCount - branch.preliminaryRounds;
    const rounds = autoRounds(teamsAfterPrelim, branch.rounds);
    let teamsInRound = teamsAfterPrelim;
    for (const round of rounds) {
      const matchups = Math.floor(teamsInRound / 2);
      const s = round.series;
      min += matchups * (s === 1 ? 1 : s);
      max += matchups * (s === 1 ? 1 : 2 * s - 1);
      teamsInRound = Math.ceil(teamsInRound / 2);
    }
  }
  // Placement matches
  for (const pm of form.value.playoffConfig.placementMatches) {
    const s = pm.series;
    min += s === 1 ? 1 : s;
    max += s === 1 ? 1 : 2 * s - 1;
  }
  return { min, max };
});

const regularSeasonMatchCount = computed(() => {
  const groups = form.value.groups;
  if (!groups || groups.length === 0) return { perGroup: 0, total: 0 };
  const rounds = form.value.regularRounds || 1;
  let total = 0;
  let minPerGroup = Infinity;
  let maxPerGroup = 0;
  for (const g of groups) {
    const n = g.teams.length;
    if (n < 2) continue;
    const matches = n * (n - 1) / 2 * rounds;
    total += matches;
    minPerGroup = Math.min(minPerGroup, matches);
    maxPerGroup = Math.max(maxPerGroup, matches);
  }
  if (minPerGroup === Infinity) minPerGroup = 0;
  return {
    perGroup: minPerGroup === maxPerGroup ? String(minPerGroup) : `${minPerGroup}–${maxPerGroup}`,
    total,
  };
});

// ── Consolation team count (mirrors backend logic) ──
const consolationTeamCount = computed(() => {
  if (!form.value.playoffConfig.consolationGroupEnabled) return 0;
  const scenarioType = form.value.playoffConfig.scenarioType;
  const branches = form.value.playoffConfig.branches;
  const advRules = form.value.playoffConfig.advancementRules;

  if (scenarioType === 'direct_playoff') {
    // Only count branches that source teams directly from groups (adv_*, advancement_rules)
    // Branches like qf_losers_b1, prelim_losers_b1 recycle losers from other branches
    const advancing = branches
      .filter(b => !b.teamsFrom || b.teamsFrom.startsWith('adv'))
      .reduce((sum, b) => sum + b.teamCount, 0);
    return Math.max(0, totalTeamCount.value - advancing);
  }

  // groups_playoff / prelim_groups_playoff: count non-advancing positions per playoff group
  const advancingPositions = new Set<number>();
  for (const rule of advRules) {
    if (rule.target?.branch_id != null && (rule.target.role === 'bye' || rule.target.role === 'prelim')) {
      const posStr = rule.source?.positions || '';
      for (const part of posStr.split(',')) {
        const trimmed = part.trim();
        if (trimmed.includes('-')) {
          const [from, to] = trimmed.split('-').map(Number);
          for (let p = from; p <= to; p++) advancingPositions.add(p);
        } else if (trimmed) {
          advancingPositions.add(Number(trimmed));
        }
      }
    }
  }
  // Fallback: from branches teamCount
  if (advancingPositions.size === 0 && branches.length > 0) {
    const mainBranch = branches.find(b => b.teamsFrom?.startsWith('adv'));
    if (mainBranch) {
      const gc = form.value.playoffConfig.groupCount || 1;
      const tpg = Math.floor(mainBranch.teamCount / gc);
      for (let p = 1; p <= tpg; p++) advancingPositions.add(p);
    }
  }

  // For groups_playoff, use playoff groups (or estimated teamsPerGroup)
  const playoffGroups = form.value.playoffGroups;
  const gc = form.value.playoffConfig.groupCount || 1;
  const tpg = teamsPerGroup.value;

  // Count non-advancing from playoff groups
  let count = 0;
  if (playoffGroups.length > 0 && playoffGroups.some(g => g.teams.length > 0)) {
    // Use actual playoff groups
    for (const g of playoffGroups) {
      for (let i = 0; i < g.teams.length; i++) {
        if (!advancingPositions.has(i + 1)) count++;
      }
    }
  } else {
    // Estimate from groupCount * teamsPerGroup
    const nonAdvancingPerGroup = Math.max(0, tpg - advancingPositions.size);
    count = gc * nonAdvancingPerGroup;
  }
  return count;
});

const consolationMatchCount = computed(() => {
  const n = consolationTeamCount.value;
  if (n < 2) return 0;
  const rounds = form.value.playoffConfig.consolationGroupRounds || 1;
  return n * (n - 1) / 2 * rounds;
});

// ── Total match count label (regular + playoff + consolation) ──
const totalMatchCountLabel = computed(() => {
  const reg = regularSeasonMatchCount.value.total;
  const cons = consolationMatchCount.value;
  const pMin = playoffMatchRange.value.min;
  const pMax = playoffMatchRange.value.max;
  const totalMin = reg + pMin + cons;
  const totalMax = reg + pMax + cons;
  return totalMin === totalMax ? String(totalMin) : `${totalMin}–${totalMax}`;
});

// ── Total clean playing time (computed per-event respecting phase settings) ──
const totalPlayingTime = computed(() => {
  const f = form.value;
  const regParts = f.partsCount || 3;
  const regPartTime = f.partTime || 20;

  // Regular: always uses base settings
  const regMinPerMatch = regParts * regPartTime;
  const regTotal = regularSeasonMatchCount.value.total * regMinPerMatch;

  // Playoff: own settings if playoffCustomSettings, else regular
  const poParts = f.playoffCustomSettings ? (f.playoffPartsCount || regParts) : regParts;
  const poPartTime = f.playoffCustomSettings ? (f.playoffPartTime || regPartTime) : regPartTime;
  const poMinPerMatch = poParts * poPartTime;
  const poMin = playoffMatchRange.value.min * poMinPerMatch;
  const poMax = playoffMatchRange.value.max * poMinPerMatch;

  // Consolation: own settings if consolationCustomSettings, else regular
  const consParts = f.consolationCustomSettings ? (f.consolationPartsCount || regParts) : regParts;
  const consPartTime = f.consolationCustomSettings ? (f.consolationPartTime || regPartTime) : regPartTime;
  const consMinPerMatch = consParts * consPartTime;
  const consTotal = consolationMatchCount.value * consMinPerMatch;

  return { min: regTotal + poMin + consTotal, max: regTotal + poMax + consTotal };
});

const formatMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const totalPlayingTimeLabel = computed(() => {
  const { min, max } = totalPlayingTime.value;
  if (min === max) return formatMinutes(min);
  return `${formatMinutes(min)}–${formatMinutes(max)}`;
});

const previewBranches = computed<PlayoffBranchType[]>(() =>
  form.value.playoffConfig.branches.map(b => ({
    id: b.id,
    name: b.name,
    color: b.color,
    teams_from: b.teamsFrom || 'advancement_rules',
    team_count: b.teamCount,
    preliminary_rounds: b.preliminaryRounds,
    preliminary_series: b.preliminarySeries,
    rounds: b.rounds.map(r => ({ name: r.name, series: r.series })),
  }))
);

const previewPlacementMatches = computed(() =>
  form.value.playoffConfig.placementMatches.map(pm => ({
    place: pm.place,
    name: pm.name,
    teams_from: pm.teamsFrom,
    series: pm.series,
  }))
);

// Scenario type selection with MH preset
const onScenarioTypeSelect = (value: ScenarioType) => {
  form.value.playoffConfig.scenarioType = value;

  if (value === 'mh_groups_playoff') {
    const pc = form.value.playoffConfig;
    // Groups
    pc.groupCount = 2;
    pc.groupFormat = 'mh';
    pc.groupAssignment = 'mh_snake';

    // Bonus points (fixed table: 1st = 11, then 7,6,5,4,3,2,1,0)
    pc.bonusEnabled = true;
    pc.bonusMethod = 'fixed';
    const teamCount = totalTeamCount.value || 9;
    const base = teamCount - 1; // e.g. 8 for 9 teams
    pc.bonusTable = Array.from({ length: teamCount }, (_, i) => ({
      position: i + 1,
      points: i === 0 ? base + 3 : Math.max(base - i, 0),
    }));

    // 1 playoff branch: 4 teams, all 1 game (semifinal + final)
    pc.branches = [{
      id: 1,
      name: t('seasonForm.branchDefaultName', { id: 1 }),
      color: 'bg',
      teamsFrom: 'custom',
      teamCount: 4,
      preliminaryRounds: 0,
      preliminarySeries: 3,
      rounds: [
        { name: t('seasonForm.semifinalRoundName'), series: 3 },
        { name: t('seasonForm.finaleRoundName'), series: 3 },
      ],
    }];

    // Placement match: 3rd place
    pc.placementMatches = [{
      place: 3,
      name: t('seasonForm.placementMatchDefaultName', { place: 3 }),
      teamsFrom: 'branch_1_sf_losers',
      series: 3,
    }];

    // Consolation group
    pc.consolationGroupEnabled = true;
    pc.consolationGroupFrom = 'all_non_advancing';
    pc.consolationGroupRounds = 2;
    pc.consolationGroupName = 'PPK pohár';
  }
};

// Branch management
let nextBranchId = 1;

const addBranch = () => {
  const id = nextBranchId++;
  form.value.playoffConfig.branches.push({
    id,
    name: t('seasonForm.branchDefaultName', { id }),
    color: 'bgr',
    teamsFrom: 'custom',
    teamCount: 4,
    preliminaryRounds: 0,
    preliminarySeries: 1,
    rounds: [{ name: t('seasonForm.finaleRoundName'), series: 1 }],
  });
};

const removeBranch = (id: number) => {
  form.value.playoffConfig.branches = form.value.playoffConfig.branches.filter(b => b.id !== id);
};

const syncBranchRounds = (branch: PlayoffBranch) => {
  const teamsAfterPrelim = branch.teamCount - branch.preliminaryRounds;
  const newRounds = autoRounds(teamsAfterPrelim, branch.rounds);
  branch.rounds = newRounds;
};

const onBracketSeriesUpdate = (payload: { roundKey: string; series: number }) => {
  // Map roundKey to round index and update ALL branches
  for (const branch of form.value.playoffConfig.branches) {
    const teamsAfterPrelim = branch.teamCount - branch.preliminaryRounds;
    const rounds = autoRounds(teamsAfterPrelim, branch.rounds);
    const roundNames = rounds.map((_, ri) => {
      const total = rounds.length;
      const fromEnd = total - 1 - ri;
      if (fromEnd === 0) return 'final';
      if (fromEnd === 1) return 'semifinal';
      if (fromEnd === 2) return 'quarterfinal';
      return `round_${ri + 1}`;
    });
    const idx = roundNames.indexOf(payload.roundKey);
    if (idx >= 0 && branch.rounds[idx]) {
      branch.rounds[idx].series = payload.series;
    }
    // Also handle preliminary
    if (payload.roundKey === 'preliminary') {
      branch.preliminarySeries = payload.series;
    }
  }
};

// Auto-sync rounds when teamCount or preliminaryRounds change
watch(() => form.value.playoffConfig.branches.map(b => `${b.teamCount}-${b.preliminaryRounds}`), () => {
  for (const branch of form.value.playoffConfig.branches) {
    syncBranchRounds(branch);
  }
}, { deep: true });

// Auto-populate bonusTable when method is 'fixed' and team count changes
watch(
  () => ({ method: form.value.playoffConfig.bonusMethod, count: totalTeamCount.value }),
  ({ method, count }) => {
    if (method !== 'fixed') return;
    const existing = form.value.playoffConfig.bonusTable;
    const newTable: { position: number; points: number }[] = [];
    for (let i = 1; i <= count; i++) {
      const prev = existing.find(r => r.position === i);
      newTable.push({ position: i, points: prev ? prev.points : Math.max(count - i, 0) });
    }
    form.value.playoffConfig.bonusTable = newTable;
  },
  { immediate: true }
);

// Advancement management
const addAdvancementRule = () => {
  form.value.playoffConfig.advancement.push({
    positions: '',
    playoffBranch: form.value.playoffConfig.branches.length > 0
      ? `branch_${form.value.playoffConfig.branches[0].id}`
      : 'branch_1',
    description: '',
  });
};

const removeAdvancementRule = (index: number) => {
  form.value.playoffConfig.advancement.splice(index, 1);
};

// Placement match management
const addPlacementMatch = () => {
  const lastPlace = form.value.playoffConfig.placementMatches.length > 0
    ? form.value.playoffConfig.placementMatches[form.value.playoffConfig.placementMatches.length - 1].place
    : 1;
  const nextPlace = lastPlace + 2;
  form.value.playoffConfig.placementMatches.push({
    place: nextPlace,
    name: t('seasonForm.placementMatchDefaultName', { place: nextPlace }),
    teamsFrom: 'branch_1_sf_losers',
    series: 1,
  });
};

const removePlacementMatch = (index: number) => {
  form.value.playoffConfig.placementMatches.splice(index, 1);
};

// Generate playoff
const isGenerating = ref(false);

const handleGeneratePlayoff = async () => {
  if (!props.season?._id || isGenerating.value) return;
  isGenerating.value = true;

  try {
    // Save first if form was modified
    if (isFormDirty.value) {
      await handleSave(true);
    }

    // Then call generate-playoff endpoint
    const cfg = form.value.playoffConfig;

    // If groups were manually edited, send them
    const manualGroups = form.value.playoffGroups
      .filter(g => g.teams.length > 0)
      .map(g => g.teams.map(e => e.id));

    const response = await axiosInstance.post(
      `/leagues/season/${props.season._id}/generate-playoff`,
      {
        playoffConfig: cfg,
        playoffGroups: manualGroups.length === cfg.groupCount ? manualGroups : undefined,
      }
    );

    // Update playoff groups in form from response
    if (response.data.groups?.length) {
      form.value.playoffGroups = response.data.groups.map((group: string[]) => ({
        teams: group.map((id: string) => ({ id, bonusPoints: 0 })),
      }));
    }

    // Emit saved to refresh parent + close all modals
    useAppStore().triggerRefetch();
    emit('saved', { ...response.data, _generated: true });
    isOpen.value = false;
  } catch (error) {
    console.error('Error generating playoff:', error);
  } finally {
    isGenerating.value = false;
  }
};

// Generate playoff group-stage events
const isGeneratingGroups = ref(false);

const canGeneratePlayoffGroups = computed(() => {
  const assignment = form.value.playoffConfig.groupAssignment;
  // Snake/draw auto-generate groups from season teams - always enabled if season has teams
  if (assignment === 'snake' || assignment === 'mh_snake' || assignment === 'draw') {
    return true;
  }
  // Fixed requires manual team assignment - need at least one group with 2+ teams
  return form.value.playoffGroups.some(g => g.teams && g.teams.length >= 2);
});

const handleGeneratePlayoffGroups = async () => {
  if (!props.season?._id || isGeneratingGroups.value) return;
  isGeneratingGroups.value = true;

  try {
    // Save first if form was modified
    if (isFormDirty.value) {
      await handleSave(true);
    }

    const response = await axiosInstance.post(
      `/leagues/season/${props.season._id}/generate-playoff-groups`
    );

    // Update playoff groups in form from response
    if (response.data.groups?.length) {
      form.value.playoffGroups = response.data.groups.map((group: string[]) => ({
        teams: group.map((id: string) => ({ id, bonusPoints: 0 })),
      }));
      // Update snapshot so form is not dirty after auto-generation
      formSnapshot.value = JSON.stringify(form.value);
    }

    useAppStore().triggerRefetch();
  } catch (error) {
    console.error('Error generating playoff groups:', error);
  } finally {
    isGeneratingGroups.value = false;
  }
};

// Generate regular season round-robin
const isGeneratingRegular = ref(false);
const generateError = ref('');

const moveTiebreaker = (index: number, direction: -1 | 1) => {
  const arr = [...form.value.tiebreakers];
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= arr.length) return;
  [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
  form.value.tiebreakers = arr;
};

const handleGenerateRegular = async () => {
  if (!props.season?._id || isGeneratingRegular.value) return;
  isGeneratingRegular.value = true;
  generateError.value = '';

  try {
    // Save first if form was modified
    if (isFormDirty.value) {
      await handleSave(true);
    }

    // Then call generate-regular endpoint
    await axiosInstance.post(
      `/leagues/season/${props.season._id}/generate-regular`
    );

    // Emit saved to refresh parent + close all modals
    useAppStore().triggerRefetch();
    emit('saved', { _id: props.season._id, _generated: true });
    isOpen.value = false;
  } catch (error: any) {
    const msg = error?.response?.data?.error;
    if (msg) {
      generateError.value = msg;
    } else {
      console.error('Error generating regular season:', error);
    }
  } finally {
    isGeneratingRegular.value = false;
  }
};

// Delete autogenerated events
const showDeleteAutogenConfirm = ref(false);
const deleteAutogenInput = ref('');

const isDeleteAutogenValid = computed(() =>
  deleteAutogenInput.value.toLowerCase() === t('delete').toLowerCase()
);

const cancelDeleteAutogen = () => {
  showDeleteAutogenConfirm.value = false;
  deleteAutogenInput.value = '';
};

const executeDeleteAutogen = async () => {
  if (!isDeleteAutogenValid.value || !props.season?._id) return;
  try {
    await axiosInstance.delete(`/leagues/season/${props.season._id}/autogenerated`);
    showDeleteAutogenConfirm.value = false;
    deleteAutogenInput.value = '';
    useAppStore().triggerRefetch();
    emit('saved', { _id: props.season._id });
  } catch (error) {
    console.error('Error deleting autogenerated events:', error);
  }
};

// Delete autogenerated playoff events
const showDeletePlayoffAutogenConfirm = ref(false);
const deletePlayoffAutogenInput = ref('');

const isDeletePlayoffAutogenValid = computed(() =>
  deletePlayoffAutogenInput.value.toLowerCase() === t('delete').toLowerCase()
);

const deletePlayoffAutogenLoading = ref(false);

const cancelDeletePlayoffAutogen = () => {
  showDeletePlayoffAutogenConfirm.value = false;
  deletePlayoffAutogenInput.value = '';
};

const executeDeletePlayoffAutogen = async () => {
  if (!isDeletePlayoffAutogenValid.value || !props.season?._id) return;
  deletePlayoffAutogenLoading.value = true;
  try {
    await axiosInstance.delete(`/leagues/season/${props.season._id}/autogenerated-playoff`);
    showDeletePlayoffAutogenConfirm.value = false;
    deletePlayoffAutogenInput.value = '';
    useAppStore().triggerRefetch();
    emit('saved', { _id: props.season._id });
  } catch (error) {
    console.error('Error deleting autogenerated playoff events:', error);
  } finally {
    deletePlayoffAutogenLoading.value = false;
  }
};

const addPlayoffGroup = () => {
  form.value.playoffGroups.push({ teams: [] as { id: string; bonusPoints: number }[] });
};

const removePlayoffGroup = (index: number) => {
  form.value.playoffGroups.splice(index, 1);
};

const openPlayoffTeamPicker = (groupIndex: number) => {
  currentPlayoffGroupIndex.value = groupIndex;
  currentView.value = 'playoffTeamPicker';
};

const allSeasonTeamIds = computed(() => {
  const ids = new Set<string>();
  form.value.groups.forEach(g => g.teams.forEach(id => ids.add(id)));
  return ids;
});

const availableTeamsForPlayoffGroup = computed(() => {
  const selectedIds = new Set<string>();
  form.value.playoffGroups.forEach(group => {
    group.teams.forEach(entry => selectedIds.add(entry.id));
  });
  return teams.value.filter(team => 
    allSeasonTeamIds.value.has(team._id) && !selectedIds.has(team._id)
  );
});

const addTeamToPlayoffGroup = (teamId: string) => {
  const group = form.value.playoffGroups[currentPlayoffGroupIndex.value];
  if (group && !group.teams.some(e => e.id === teamId)) {
    group.teams.push({ id: teamId, bonusPoints: 0 });
  }
  currentView.value = 'main';
};

const removeTeamFromPlayoffGroup = (groupIndex: number, teamId: string) => {
  const group = form.value.playoffGroups[groupIndex];
  if (group) {
    group.teams = group.teams.filter(e => e.id !== teamId);
  }
};

const availableTeamsForGroup = computed(() => {
  const selectedIds = new Set<string>();
  form.value.groups.forEach((group, index) => {
    group.teams.forEach((id: string) => selectedIds.add(id));
  });
  return teams.value.filter(team => !selectedIds.has(team._id));
});

const addTeamToGroup = (teamId: string) => {
  const group = form.value.groups[currentGroupIndex.value];
  if (group && !group.teams.includes(teamId)) {
    group.teams.push(teamId);
  }
  currentView.value = 'main';
};

const removeTeamFromGroup = (groupIndex: number, teamId: string) => {
  const group = form.value.groups[groupIndex];
  if (group) {
    group.teams = group.teams.filter(id => id !== teamId);
  }
};

// Save
const handleSave = async (keepOpen = false) => {
  if (isSaving.value) return;
  isSaving.value = true;

  try {
    // Filter out empty groups and transform to backend format
    const nonEmptyGroups = form.value.groups.filter(g => g.teams && g.teams.length > 0);
    const transformedGroups = nonEmptyGroups.map(group =>
      group.teams.map(teamId => ({ team: teamId, seasonData: {} }))
    );

    // Transform playoff groups
    const nonEmptyPlayoffGroups = form.value.playoffGroups.filter(g => g.teams && g.teams.length > 0);
    const transformedPlayoffGroups = nonEmptyPlayoffGroups.map(group =>
      group.teams.map(entry => ({ team: entry.id, seasonData: { bonusPoints: entry.bonusPoints || 0 } }))
    );

    const seasonData: any = {
      seasonType: form.value.seasonType,
      dateFrom: form.value.dateFrom,
      dateTo: form.value.dateTo,
      active: form.value.active,
      partsCount: form.value.partsCount,
      partTime: form.value.partTime,
      playersCount: form.value.playersCount,
      overtimeDuration: form.value.overtimeDuration,
      penaltySmall: form.value.penaltySmall,
      penaltyBig: form.value.penaltyBig,
      penaltyPersonal: form.value.penaltyPersonal,
      registrationFeePlayer: form.value.registrationFeePlayer,
      registrationFeeGoalie: form.value.registrationFeeGoalie,
      registrationFeeTeam: form.value.registrationFeeTeam,
      splitPointsByParts: form.value.splitPointsByParts,
      pointsWin: form.value.pointsWin,
      pointsOvertimeWin: form.value.pointsOvertimeWin,
      pointsOvertimeLoss: form.value.pointsOvertimeLoss,
      regularRounds: form.value.regularRounds,
      tiebreakers: form.value.tiebreakers,
      groups: transformedGroups,
      content: form.value.content,
      _league: props.leagueId,
      playoffFormat: form.value.playoffFormat,
      playoffBestOf: form.value.playoffBestOf,
      preRound: form.value.preRound,
      playoffGroupsActive: form.value.playoffGroupsActive,
      playoffActive: form.value.playoffActive,
      playoffCustomSettings: form.value.playoffCustomSettings,
      playoffPartsCount: form.value.playoffCustomSettings ? form.value.playoffPartsCount : form.value.partsCount,
      playoffPartTime: form.value.playoffCustomSettings ? form.value.playoffPartTime : form.value.partTime,
      playoffPlayersCount: form.value.playoffCustomSettings ? form.value.playoffPlayersCount : form.value.playersCount,
      playoffOvertimeDuration: form.value.playoffCustomSettings ? form.value.playoffOvertimeDuration : form.value.overtimeDuration,
      playoffPenaltySmall: form.value.playoffCustomSettings ? form.value.playoffPenaltySmall : form.value.penaltySmall,
      playoffPenaltyBig: form.value.playoffCustomSettings ? form.value.playoffPenaltyBig : form.value.penaltyBig,
      playoffPenaltyPersonal: form.value.playoffCustomSettings ? form.value.playoffPenaltyPersonal : form.value.penaltyPersonal,
      playoffSplitPointsByParts: form.value.playoffCustomSettings ? form.value.playoffSplitPointsByParts : form.value.splitPointsByParts,
      playoffPointsWin: form.value.playoffCustomSettings ? form.value.playoffPointsWin : form.value.pointsWin,
      playoffPointsOvertimeWin: form.value.playoffCustomSettings ? form.value.playoffPointsOvertimeWin : form.value.pointsOvertimeWin,
      playoffPointsOvertimeLoss: form.value.playoffCustomSettings ? form.value.playoffPointsOvertimeLoss : form.value.pointsOvertimeLoss,
      consolationCustomSettings: form.value.consolationCustomSettings,
      consolationPartsCount: form.value.consolationCustomSettings ? form.value.consolationPartsCount : form.value.partsCount,
      consolationPartTime: form.value.consolationCustomSettings ? form.value.consolationPartTime : form.value.partTime,
      consolationPlayersCount: form.value.consolationCustomSettings ? form.value.consolationPlayersCount : form.value.playersCount,
      consolationOvertimeDuration: form.value.consolationCustomSettings ? form.value.consolationOvertimeDuration : form.value.overtimeDuration,
      consolationPenaltySmall: form.value.consolationCustomSettings ? form.value.consolationPenaltySmall : form.value.penaltySmall,
      consolationPenaltyBig: form.value.consolationCustomSettings ? form.value.consolationPenaltyBig : form.value.penaltyBig,
      consolationPenaltyPersonal: form.value.consolationCustomSettings ? form.value.consolationPenaltyPersonal : form.value.penaltyPersonal,
      consolationSplitPointsByParts: form.value.consolationCustomSettings ? form.value.consolationSplitPointsByParts : form.value.splitPointsByParts,
      consolationPointsWin: form.value.consolationCustomSettings ? form.value.consolationPointsWin : form.value.pointsWin,
      consolationPointsOvertimeWin: form.value.consolationCustomSettings ? form.value.consolationPointsOvertimeWin : form.value.pointsOvertimeWin,
      consolationPointsOvertimeLoss: form.value.consolationCustomSettings ? form.value.consolationPointsOvertimeLoss : form.value.pointsOvertimeLoss,
      groupsCustomSettings: form.value.groupsCustomSettings,
      groupsPartsCount: form.value.groupsCustomSettings ? form.value.groupsPartsCount : form.value.partsCount,
      groupsPartTime: form.value.groupsCustomSettings ? form.value.groupsPartTime : form.value.partTime,
      groupsPlayersCount: form.value.groupsCustomSettings ? form.value.groupsPlayersCount : form.value.playersCount,
      groupsOvertimeDuration: form.value.groupsCustomSettings ? form.value.groupsOvertimeDuration : form.value.overtimeDuration,
      groupsPenaltySmall: form.value.groupsCustomSettings ? form.value.groupsPenaltySmall : form.value.penaltySmall,
      groupsPenaltyBig: form.value.groupsCustomSettings ? form.value.groupsPenaltyBig : form.value.penaltyBig,
      groupsPenaltyPersonal: form.value.groupsCustomSettings ? form.value.groupsPenaltyPersonal : form.value.penaltyPersonal,
      groupsSplitPointsByParts: form.value.groupsCustomSettings ? form.value.groupsSplitPointsByParts : form.value.splitPointsByParts,
      groupsPointsWin: form.value.groupsCustomSettings ? form.value.groupsPointsWin : form.value.pointsWin,
      groupsPointsOvertimeWin: form.value.groupsCustomSettings ? form.value.groupsPointsOvertimeWin : form.value.pointsOvertimeWin,
      groupsPointsOvertimeLoss: form.value.groupsCustomSettings ? form.value.groupsPointsOvertimeLoss : form.value.pointsOvertimeLoss,
      playoffGroups: transformedPlayoffGroups,
      playoffConfig: form.value.playoffConfig,
    };

    if (props.localMode) {
      // Local mode — don't call API, just emit the data
      const localSeason = {
        ...seasonData,
        _localId: props.season?._localId || crypto.randomUUID(),
        year: buildYear(seasonData.dateFrom, seasonData.dateTo),
      };
      emit('saved', localSeason);
      if (!keepOpen) isOpen.value = false;
    } else {
      let response;
      if (isEditMode.value && props.season?._id) {
        response = await axiosInstance.put(`/leagues/season/${props.season._id}`, seasonData);
      } else {
        response = await axiosInstance.post('/leagues/season', seasonData);
      }

      emit('saved', response.data);
      useAppStore().triggerRefetch();
      if (!keepOpen) isOpen.value = false;
    }

    // Update snapshot so isFormDirty reflects the saved state
    formSnapshot.value = JSON.stringify(form.value);
  } catch (error) {
    console.error('Error saving season:', error);
  } finally {
    isSaving.value = false;
  }
};

const buildYear = (dateFrom: string, dateTo: string) => {
  if (!dateFrom || !dateTo) return '';
  const dFrom = new Date(dateFrom);
  const dTo = new Date(dateTo);
  return dFrom.getFullYear() !== dTo.getFullYear()
    ? `${dFrom.getFullYear()}-${String(dTo.getFullYear()).slice(-2)}`
    : `${dFrom.getFullYear()}`;
};

const handleClose = () => {
  currentView.value = 'main';
};

// Populate form when editing
const populateForm = (season: ISeason) => {
  isPopulating.value = true;
  const groups = (season.groups || []).map((group: any) => {
    if (Array.isArray(group)) {
      return { teams: group.map((entry: any) => typeof entry === 'object' ? (entry.team?._id || entry.team) : entry) };
    }
    if (group && group.teams) {
      return { teams: group.teams.map((t: any) => typeof t === 'object' ? (t._id || t) : t) };
    }
    return { teams: [] };
  });

  const playoffGroups = (season.playoffGroups || []).map((group: any) => {
    if (Array.isArray(group)) {
      return { teams: group.map((entry: any) => {
        const id = typeof entry === 'object' ? (entry.team?._id || entry.team) : entry;
        const bonusPoints = typeof entry === 'object' ? (entry.seasonData?.bonusPoints ?? 0) : 0;
        return { id: String(id), bonusPoints };
      }) };
    }
    if (group && group.teams) {
      return { teams: group.teams.map((t: any) => {
        const id = typeof t === 'object' ? (t._id || t) : t;
        return { id: String(id), bonusPoints: 0 };
      }) };
    }
    return { teams: [] as { id: string; bonusPoints: number }[] };
  });

  form.value = {
    seasonType: (season as any).seasonType || 'league',
    dateFrom: season.dateFrom || formatDate(new Date()),
    dateTo: season.dateTo || formatDate(new Date()),
    active: season.active ?? true,
    partsCount: season.partsCount || 3,
    partTime: season.partTime || 15,
    playersCount: season.playersCount || '5+1',
    overtimeDuration: season.overtimeDuration ?? 10,
    penaltySmall: season.penaltySmall ?? 1.5,
    penaltyBig: season.penaltyBig ?? 3,
    penaltyPersonal: season.penaltyPersonal ?? 7,
    registrationFeePlayer: season.registrationFeePlayer ?? 0,
    registrationFeeGoalie: season.registrationFeeGoalie ?? 0,
    registrationFeeTeam: (season as any).registrationFeeTeam ?? 0,
    splitPointsByParts: season.splitPointsByParts ?? false,
    pointsWin: season.pointsWin ?? 3,
    pointsOvertimeWin: season.pointsOvertimeWin ?? 2,
    pointsOvertimeLoss: season.pointsOvertimeLoss ?? 1,
    regularRounds: (season as any).regularRounds ?? 1,
    tiebreakers: (season as any).tiebreakers ?? ['h2h_points', 'h2h_goals', 'goal_diff', 'goals_for', 'lot'],
    groups: groups.length > 0 ? groups : [{ teams: [] }],
    content: season.content || '',
    _league: (typeof season._league === 'string' ? season._league : season._league?._id) || props.leagueId,
    playoffFormat: season.playoffFormat || '',
    playoffBestOf: season.playoffBestOf ?? 5,
    preRound: season.preRound ?? false,
    playoffGroupsActive: season.playoffGroupsActive ?? false,
    playoffActive: season.playoffActive ?? false,
    playoffCustomSettings: (season as any).playoffCustomSettings ?? false,
    playoffPartsCount: (season as any).playoffPartsCount ?? 3,
    playoffPartTime: (season as any).playoffPartTime ?? 15,
    playoffPlayersCount: (season as any).playoffPlayersCount || '5+1',
    playoffOvertimeDuration: (season as any).playoffOvertimeDuration ?? 10,
    playoffPenaltySmall: (season as any).playoffPenaltySmall ?? 1.5,
    playoffPenaltyBig: (season as any).playoffPenaltyBig ?? 3,
    playoffPenaltyPersonal: (season as any).playoffPenaltyPersonal ?? 7,
    playoffSplitPointsByParts: (season as any).playoffSplitPointsByParts ?? false,
    playoffPointsWin: (season as any).playoffPointsWin ?? 3,
    playoffPointsOvertimeWin: (season as any).playoffPointsOvertimeWin ?? 2,
    playoffPointsOvertimeLoss: (season as any).playoffPointsOvertimeLoss ?? 1,
    consolationCustomSettings: (season as any).consolationCustomSettings ?? false,
    consolationPartsCount: (season as any).consolationPartsCount ?? 3,
    consolationPartTime: (season as any).consolationPartTime ?? 15,
    consolationPlayersCount: (season as any).consolationPlayersCount || '5+1',
    consolationOvertimeDuration: (season as any).consolationOvertimeDuration ?? 10,
    consolationPenaltySmall: (season as any).consolationPenaltySmall ?? 1.5,
    consolationPenaltyBig: (season as any).consolationPenaltyBig ?? 3,
    consolationPenaltyPersonal: (season as any).consolationPenaltyPersonal ?? 7,
    consolationSplitPointsByParts: (season as any).consolationSplitPointsByParts ?? false,
    consolationPointsWin: (season as any).consolationPointsWin ?? 3,
    consolationPointsOvertimeWin: (season as any).consolationPointsOvertimeWin ?? 2,
    consolationPointsOvertimeLoss: (season as any).consolationPointsOvertimeLoss ?? 1,
    groupsCustomSettings: (season as any).groupsCustomSettings ?? false,
    groupsPartsCount: (season as any).groupsPartsCount ?? 3,
    groupsPartTime: (season as any).groupsPartTime ?? 15,
    groupsPlayersCount: (season as any).groupsPlayersCount || '5+1',
    groupsOvertimeDuration: (season as any).groupsOvertimeDuration ?? 10,
    groupsPenaltySmall: (season as any).groupsPenaltySmall ?? 1.5,
    groupsPenaltyBig: (season as any).groupsPenaltyBig ?? 3,
    groupsPenaltyPersonal: (season as any).groupsPenaltyPersonal ?? 7,
    groupsSplitPointsByParts: (season as any).groupsSplitPointsByParts ?? false,
    groupsPointsWin: (season as any).groupsPointsWin ?? 3,
    groupsPointsOvertimeWin: (season as any).groupsPointsOvertimeWin ?? 2,
    groupsPointsOvertimeLoss: (season as any).groupsPointsOvertimeLoss ?? 1,
    playoffGroups: playoffGroups.length > 0 ? playoffGroups : [{ teams: [] }],
    playoffConfig: season.playoffConfig ? {
      scenarioType: season.playoffConfig.scenarioType || '',
      groupCount: season.playoffConfig.groupCount ?? 4,
      groupFormat: season.playoffConfig.groupFormat || 'rr2',
      groupScoring: (season.playoffConfig as any).groupScoring || '3-2-1-0',
      groupAssignment: season.playoffConfig.groupAssignment || 'snake',
      tiebreakMiniTable: (season.playoffConfig as any).tiebreakMiniTable ?? true,
      advancement: (season.playoffConfig as any).advancement || [],
      advancementRules: (season.playoffConfig as any).advancementRules || [],
      consolationGroupEnabled: (season.playoffConfig as any).consolationGroupEnabled ?? false,
      consolationGroupFrom: (['all_non_advancing'].includes((season.playoffConfig as any).consolationGroupFrom)) ? (season.playoffConfig as any).consolationGroupFrom : 'all_non_advancing',
      consolationGroupName: (season.playoffConfig as any).consolationGroupName || t('seasonForm.consolationGroupNameDefault'),
      consolationGroupRounds: (season.playoffConfig as any).consolationGroupRounds ?? ((season.playoffConfig as any).consolationGroupFormat === 'rr2' ? 2 : 1),
      bonusEnabled: season.playoffConfig.bonusEnabled ?? false,
      bonusMethod: season.playoffConfig.bonusMethod || 'linear',
      bonusTopValue: season.playoffConfig.bonusTopValue ?? 8,
      bonusTable: season.playoffConfig.bonusTable || [],
      branches: (season.playoffConfig as any).branches || [],
      placementMatches: (season.playoffConfig as any).placementMatches || [],
    } : getDefaultForm().playoffConfig,
  };

  // Update nextBranchId based on loaded branches
  const maxBranchId = form.value.playoffConfig.branches.reduce((max: number, b: PlayoffBranch) => Math.max(max, b.id), 0);
  nextBranchId = maxBranchId + 1;
};

// When playoffCustomSettings is first turned ON, copy regular values as starting point
watch(() => form.value.playoffCustomSettings, (val, oldVal) => {
  if (val && !oldVal && !isPopulating.value) {
    form.value.playoffPartsCount = form.value.partsCount;
    form.value.playoffPartTime = form.value.partTime;
    form.value.playoffPlayersCount = form.value.playersCount;
    form.value.playoffOvertimeDuration = form.value.overtimeDuration;
    form.value.playoffPenaltySmall = form.value.penaltySmall;
    form.value.playoffPenaltyBig = form.value.penaltyBig;
    form.value.playoffPenaltyPersonal = form.value.penaltyPersonal;
    form.value.playoffSplitPointsByParts = form.value.splitPointsByParts;
    form.value.playoffPointsWin = form.value.pointsWin;
    form.value.playoffPointsOvertimeWin = form.value.pointsOvertimeWin;
    form.value.playoffPointsOvertimeLoss = form.value.pointsOvertimeLoss;
  }
});

// When consolationCustomSettings is first turned ON, copy regular values as starting point
watch(() => form.value.consolationCustomSettings, (val, oldVal) => {
  if (val && !oldVal && !isPopulating.value) {
    form.value.consolationPartsCount = form.value.partsCount;
    form.value.consolationPartTime = form.value.partTime;
    form.value.consolationPlayersCount = form.value.playersCount;
    form.value.consolationOvertimeDuration = form.value.overtimeDuration;
    form.value.consolationPenaltySmall = form.value.penaltySmall;
    form.value.consolationPenaltyBig = form.value.penaltyBig;
    form.value.consolationPenaltyPersonal = form.value.penaltyPersonal;
    form.value.consolationSplitPointsByParts = form.value.splitPointsByParts;
    form.value.consolationPointsWin = form.value.pointsWin;
    form.value.consolationPointsOvertimeWin = form.value.pointsOvertimeWin;
    form.value.consolationPointsOvertimeLoss = form.value.pointsOvertimeLoss;
  }
});

// When groupsCustomSettings is first turned ON, copy regular values as starting point
watch(() => form.value.groupsCustomSettings, (val, oldVal) => {
  if (val && !oldVal && !isPopulating.value) {
    form.value.groupsPartsCount = form.value.partsCount;
    form.value.groupsPartTime = form.value.partTime;
    form.value.groupsPlayersCount = form.value.playersCount;
    form.value.groupsOvertimeDuration = form.value.overtimeDuration;
    form.value.groupsPenaltySmall = form.value.penaltySmall;
    form.value.groupsPenaltyBig = form.value.penaltyBig;
    form.value.groupsPenaltyPersonal = form.value.penaltyPersonal;
    form.value.groupsSplitPointsByParts = form.value.splitPointsByParts;
    form.value.groupsPointsWin = form.value.pointsWin;
    form.value.groupsPointsOvertimeWin = form.value.pointsOvertimeWin;
    form.value.groupsPointsOvertimeLoss = form.value.pointsOvertimeLoss;
  }
});

// Watch modal open
watch(isOpen, async (val) => {
  if (val) {
    currentView.value = 'main';
    await fetchTeams();
    
    if (props.season) {
      populateForm(props.season);
    } else {
      form.value = getDefaultForm();
    }
    // Wait for watchers to flush before clearing the flag
    await nextTick();
    isPopulating.value = false;
    formSnapshot.value = JSON.stringify(form.value);
  }
});
</script>

<style lang="scss" scoped>
.views-container {
  position: relative;
  overflow: hidden;
  min-height: calc(92vh - 90px);
}

.main-view {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1), 
              opacity 0.25s ease;
  
  &--hidden {
    opacity: 0.3;
    transform: translateX(-100px) scale(0.92);
    pointer-events: none;
  }
}

// ── Season Form Tabs ──
.sf-tabs {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  background: rgba(var(--v-theme-sheet), 1);
  border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.08);
  margin: 0 -16px;
  padding: 0 16px;
}

.sf-tab {
  position: relative;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(var(--v-theme-sheetText), 0.45);
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
  white-space: nowrap;

  &:hover {
    color: rgba(var(--v-theme-sheetText), 0.7);
  }

  &--active {
    color: rgb(var(--v-theme-ice));
  }
}

.sf-tabs__indicator {
  position: absolute;
  bottom: -1px;
  left: 16px;
  height: 2px;
  background: rgb(var(--v-theme-ice));
  border-radius: 2px 2px 0 0;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}

.sf-tab-content {
  padding-top: 24px;
}

// Slide transition
.slide-enter-active {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}

.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}

.slide-enter-from {
  transform: translateX(100%) !important;
}

.slide-leave-to {
  transform: translateX(100%) !important;
}

.subview {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  max-width: 600px;
  height: 92vh;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-sheetBackground));
  border-radius: 32px 32px 0 0;
  z-index: 10000;
  overflow: hidden;
  transform: translateX(0);
}

.subview-header {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  z-index: 10;
  pointer-events: none;
  
  > * {
    pointer-events: auto;
  }
}

.subview-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px;
  padding-top: 70px;
}

.back-btn {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: rgb(var(--v-theme-sheetCloseBtn));
  cursor: pointer;
  transition: all 0.2s ease;
  
  :deep(.v-icon) {
    color: rgb(var(--v-theme-sheetText)) !important;
  }
  
  &:hover {
    filter: brightness(0.92);
  }
  
  &:active {
    transform: scale(0.95);
  }
}

.subview-title {
  flex: 1;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
}

.spacer {
  width: 45px;
}

.search-empty {
  padding: 24px;
  text-align: center;
  color: rgba(var(--v-theme-sheetText), 0.5);
}

// Date input
.date-input {
  background: transparent;
  border: none;
  outline: none;
  font-size: 0.95rem;
  color: rgb(var(--v-theme-sheetText));
  text-align: right;
  
  &::-webkit-calendar-picker-indicator {
    filter: invert(0.5);
  }
}

// Number input
.number-input {
  background: transparent;
  border: none;
  outline: none;
  font-size: 0.95rem;
  color: rgb(var(--v-theme-sheetText));
  text-align: right;
  width: 80px;
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
  }
}

// Groups
.group-section {
  padding: 8px 0;
  
  & + .group-section {
    border-top: 1px solid rgba(var(--v-theme-sheetText), 0.1);
  }
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
}

.group-label {
  font-weight: 600;
  font-size: 0.95rem;
  color: rgb(var(--v-theme-sheetText));
}

.group-remove-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:active {
    background: rgba(var(--v-theme-sheetText), 0.1);
  }
}

.group-teams {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 16px;
}

.group-team-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  border-radius: 12px;
  font-size: 0.85rem;
  color: rgb(var(--v-theme-sheetText));
}

.team-chip-logo {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  object-fit: cover;
}

.team-chip-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.team-chip-remove {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  opacity: 0.6;
  
  &:hover {
    opacity: 1;
  }
}

.bonus-input {
  width: 32px;
  text-align: center;
  background: rgba(var(--v-theme-sheetText), 0.1);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.15);
  border-radius: 6px;
  color: rgb(var(--v-theme-sheetText));
  font-size: 0.75rem;
  padding: 2px 0;
  -moz-appearance: textfield;
  appearance: textfield;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    border-color: rgb(var(--v-theme-primary));
  }
}

.team-pick-logo {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  object-fit: cover;
}

.group-empty {
  padding: 12px 16px;
  color: rgba(var(--v-theme-sheetText), 0.4);
  font-size: 0.9rem;
}

// Content editor
.content-editor-wrapper {
  padding: 12px 16px;
}

.content-textarea {
  width: 100%;
  min-height: 120px;
  background: rgba(var(--v-theme-sheetText), 0.05);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.1);
  border-radius: 12px;
  padding: 12px;
  font-size: 0.95rem;
  color: rgb(var(--v-theme-sheetText));
  outline: none;
  resize: vertical;
  font-family: inherit;
  
  &::placeholder {
    color: rgba(var(--v-theme-sheetText), 0.4);
  }
  
  &:focus {
    border-color: rgba(var(--v-theme-sheetText), 0.3);
  }
}

.deactivate-confirm {
  text-align: center;

  &__icon {
    margin-bottom: 12px;
  }

  &__title {
    font-size: 17px;
    font-weight: 600;
    color: rgba(var(--v-theme-sheetText), 1);
    margin-bottom: 8px;
  }

  &__text {
    font-size: 14px;
    color: rgba(var(--v-theme-sheetText), 0.6);
    line-height: 1.4;
    margin-bottom: 20px;
  }

  &__actions {
    display: flex;
    gap: 10px;
  }

  &__btn {
    flex: 1;
    padding: 10px 16px;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    border: none;
    cursor: pointer;

    &--cancel {
      background: rgba(var(--v-theme-sheetText), 0.1);
      color: rgba(var(--v-theme-sheetText), 0.8);
    }

    &--confirm {
      background: #e53935;
      color: #fff;
    }
  }
}

// Series rounds
.series-rounds-header {
  padding: 12px 16px 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.series-round-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;

  & + .series-round-row {
    border-top: 1px solid rgba(var(--v-theme-sheetText), 0.06);
  }
}

.series-round-name {
  font-size: 0.95rem;
  color: rgb(var(--v-theme-sheetText));
}

.series-opts-row {
  display: flex;
  gap: 6px;
}

.series-opt-btn {
  min-width: 36px;
  height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.15);
  background: transparent;
  color: rgba(var(--v-theme-sheetText), 0.7);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &--active {
    background: rgba(var(--v-theme-primary), 0.15);
    border-color: rgb(var(--v-theme-primary));
    color: rgb(var(--v-theme-primary));
    font-weight: 600;
  }
}

// Generate playoff
.generate-playoff-wrap {
  padding: 16px;
}

.generate-playoff-btn {
  width: 100%;
  padding: 14px;
  background: rgb(var(--v-theme-primary));
  color: #fff;
  border: none;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    transform: scale(0.97);
  }
}

.generate-playoff-hint {
  text-align: center;
  font-size: 0.8rem;
  color: rgba(var(--v-theme-sheetText), 0.45);
  margin-top: 8px;
}

.generate-error {
  text-align: center;
  font-size: 0.8rem;
  color: rgb(var(--v-theme-error));
  margin-top: 8px;
}

.tiebreakers-list {
  padding: 4px 0;
}

.tiebreaker-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 0.5px solid rgba(var(--v-theme-sheetText), 0.08);

  &:last-child {
    border-bottom: none;
  }
}

.tiebreaker-index {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  min-width: 24px;
}

.tiebreaker-label {
  flex: 1;
  font-size: 0.9rem;
  color: rgba(var(--v-theme-sheetText), 0.9);
}

.tiebreaker-actions {
  display: flex;
  gap: 2px;
}

.tiebreaker-btn {
  background: rgba(var(--v-theme-sheetText), 0.06);
  border: none;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(var(--v-theme-sheetText), 0.5);
  transition: all 0.15s ease;

  &:active {
    background: rgba(var(--v-theme-sheetText), 0.12);
  }
}

.generate-playoff-btn--delete {
  background: transparent;
  color: rgb(var(--v-theme-error));
  border: 1.5px solid rgba(var(--v-theme-error), 0.3);
  margin-top: 10px;
  font-size: 0.9rem;
  padding: 10px;
}

// Delete autogenerated confirm dialog
.delete-confirm-dialog {
  padding: 24px;
  text-align: center;
}
.delete-confirm-icon {
  margin-bottom: 16px;
}
.delete-confirm-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}
.delete-confirm-subtitle {
  font-size: 0.9rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-bottom: 16px;
}
.delete-confirm-input-wrap {
  margin-bottom: 20px;
}
.delete-confirm-input {
  width: 100%;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.15);
  font-size: 0.95rem;
  text-align: center;
  background: transparent;
  color: inherit;
  outline: none;
  transition: border-color 0.2s;
  &:focus {
    border-color: rgb(var(--v-theme-error));
  }
}
.delete-confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}
.delete-confirm-btn {
  padding: 10px 24px;
  border-radius: 12px;
  border: none;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &--cancel {
    background: rgba(var(--v-theme-on-surface), 0.1);
    color: inherit;
  }
  &--confirm {
    background: rgb(var(--v-theme-error));
    color: white;
    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }
  &:active:not(:disabled) {
    transform: scale(0.96);
  }
}

// Playoff sub-headers
.playoff-sub-header {
  padding: 14px 16px 6px;
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.45);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-top: 1px solid rgba(var(--v-theme-sheetText), 0.06);
  margin-top: 4px;
}

// Advancement
.advancement-wrap {
  padding: 0 16px 8px;
}

.advancement-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;

  & + .advancement-row {
    border-top: 1px solid rgba(var(--v-theme-sheetText), 0.04);
  }
}

.advancement-row__fields {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  flex: 1;
}

.advancement-field {
  display: flex;
  align-items: center;
  gap: 4px;
}

.advancement-field__label {
  font-size: 0.8rem;
  color: rgba(var(--v-theme-sheetText), 0.5);
  white-space: nowrap;
}

.adv-input {
  width: 56px;
  padding: 4px 6px;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.12);
  border-radius: 8px;
  font-size: 0.85rem;
  background: rgba(var(--v-theme-sheetText), 0.03);
  color: rgba(var(--v-theme-sheetText), 0.85);
  text-align: center;

  &.adv-desc {
    width: 100px;
    text-align: left;
  }
}

.adv-select {
  padding: 4px 6px;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.12);
  border-radius: 8px;
  font-size: 0.82rem;
  background: rgba(var(--v-theme-sheetText), 0.03);
  color: rgba(var(--v-theme-sheetText), 0.85);
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
}

.adv-remove {
  background: none;
  border: none;
  color: rgba(var(--v-theme-sheetText), 0.35);
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;

  &:hover {
    color: rgb(var(--v-theme-error));
  }
}

// Branch cards
.branch-card {
  margin: 8px 16px;
  background: rgba(var(--v-theme-sheetText), 0.02);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.06);
  border-left: 4px solid var(--branch-color, rgba(var(--v-theme-sheetText), 0.12));
  border-radius: 12px;
  padding: 12px;
}

.branch-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.branch-name-input {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.12);
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  background: transparent;
  color: rgba(var(--v-theme-sheetText), 0.85);
}

.branch-card__fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 10px;
}

.branch-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.branch-field__label {
  font-size: 0.72rem;
  color: rgba(var(--v-theme-sheetText), 0.45);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

// Branch rounds
.branch-rounds {
  border-top: 1px solid rgba(var(--v-theme-sheetText), 0.06);
  padding-top: 8px;
  margin-top: 4px;
}

.branch-rounds__title {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.45);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 6px;
}

.branch-round-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;

  & + .branch-round-row {
    border-top: 1px solid rgba(var(--v-theme-sheetText), 0.03);
  }
}

.branch-round-name {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.12);
  border-radius: 8px;
  font-size: 0.85rem;
  background: transparent;
  color: rgba(var(--v-theme-sheetText), 0.85);
}

.series-opts-row {
  display: flex;
  gap: 2px;
}

.series-opt-btn {
  padding: 4px 8px;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.1);
  border-radius: 6px;
  font-size: 0.78rem;
  background: transparent;
  color: rgba(var(--v-theme-sheetText), 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &--active {
    background: rgb(var(--v-theme-primary));
    color: #fff;
    border-color: rgb(var(--v-theme-primary));
  }
}

// Flow preview
.flow-preview {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 0 2px;
  overflow-x: auto;
}

.fbox {
  padding: 4px 8px;
  background: rgba(var(--v-theme-sheetText), 0.04);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.08);
  border-radius: 6px;
  font-size: 0.72rem;
  color: rgba(var(--v-theme-sheetText), 0.6);
  white-space: nowrap;
}

.farr {
  font-size: 1rem;
  color: rgba(var(--v-theme-sheetText), 0.25);
}

// Placement matches
.pm-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;

  & + .pm-row {
    border-top: 1px solid rgba(var(--v-theme-sheetText), 0.04);
  }
}

.pm-name-input {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.12);
  border-radius: 8px;
  font-size: 0.85rem;
  background: transparent;
  color: rgba(var(--v-theme-sheetText), 0.85);
  min-width: 0;
}

.pm-place-input {
  width: 32px;
  padding: 0 2px;
  border: none;
  border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.25);
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: center;
  -moz-appearance: textfield;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.pm-field {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pm-field__label {
  font-size: 0.78rem;
  color: rgba(var(--v-theme-sheetText), 0.45);
  white-space: nowrap;
}

.pm-series-select {
  width: 70px;
}

// Stats
.season-stats {
  margin: 12px 16px;
  background: rgba(var(--v-theme-sheetText), 0.02);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.06);
  border-radius: 12px;
  overflow: hidden;
}

.season-stats__section {
  padding: 10px 16px;
  & + & {
    border-top: 1px solid rgba(var(--v-theme-sheetText), 0.06);
  }
}

.season-stats__header {
  font-size: 0.62rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(var(--v-theme-sheetText), 0.3);
  margin-bottom: 6px;
}

.season-stats__section--total {
  background: rgba(var(--v-theme-sheetText), 0.03);
  .playoff-stat__number {
    font-size: 1.3rem;
  }
}

.season-stats__grid {
  display: flex;
  justify-content: space-around;
}

.playoff-stat {
  text-align: center;
}

.playoff-stat__number {
  font-size: 1.1rem;
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.85);
}

.playoff-stat__label {
  font-size: 0.68rem;
  color: rgba(var(--v-theme-sheetText), 0.4);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 2px;
}

// Badge
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;

  &.bg { background: #4caf50; }
  &.ba { background: #ff9800; }
  &.bt { background: #009688; }
  &.bpk { background: #e91e63; }
  &.bgr { background: #9e9e9e; }
}
</style>
